import { BlogNewsletterSignupSection } from "@/components/blog/BlogNewsletterSignupSection";
import { CelebrationIcon } from "@/components/icons/CelebrationIcon";
import { Footer } from "@/components/layout/Footer";
import { Navbar } from "@/components/layout/Navbar";
import { useSEO } from "@/hooks/useSEO";
import { useHomepageVideos } from "@/hooks/useHomepageVideos";
import { isSameAddOn } from "@/lib/addOnIdentity";
import { DEFAULT_CUSTOM_MIX_ADD_ON, DEFAULT_VIRTUAL_PRACTICE_ADD_ON } from "@/lib/api";
import { useAppStore } from "@/lib/store";
import frameCenter from "@/assets/hero/frame-center.png";
import frameLeft from "@/assets/hero/frame-left.png";
import frameRight from "@/assets/hero/frame-right.png";
import { useCallback, useEffect, useMemo, useRef, useState, type TouchEvent } from "react";
import { Maximize2, Minimize2, Music, Pause, Play, Sparkles, Volume2, VolumeX } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

const fallbackPosters = [frameLeft, frameCenter, frameRight];

type HeroVideoSource = {
  poster: string;
  src: string;
  mobileSrc?: string;
  playbackSrc?: string;
};

const defaultHeroVideos = [
  {
    poster: frameLeft,
    src: "https://cdn.prod.website-files.com/686229256a807206c1874545%2F68ba785fc21b7dcb88917ec7_new_homepage_3%20%28720p%29-transcode.mp4",
    playbackSrc:
      "https://www.dropbox.com/scl/fi/myp0y58cz39z2196owp2e/new_homepage_3-720p.mp4?rlkey=cyuqr226bq341i668q8dhqy55&st=dw2z1e07&raw=1",
  },
  {
    poster: frameCenter,
    src: "https://cdn.prod.website-files.com/686229256a807206c1874545%2F68bacb48c6ea39b95fef8c2f_replacement_for_homepage_1%20%28720p%29-transcode.mp4",
    playbackSrc:
      "https://www.dropbox.com/scl/fi/p7zuoyllac6ttonnkl0vb/replacement_for_homepage_1-720p.mp4?rlkey=0iy52xv4t82c9xzelq6x4dpsq&st=d6pwmg54&raw=1",
  },
  {
    poster: frameRight,
    src: "https://cdn.prod.website-files.com/686229256a807206c1874545%2F68ba6f3917d77bde3060c093_new_homepage_2%20%28720p%29-transcode.mp4",
    playbackSrc:
      "https://www.dropbox.com/scl/fi/vu0pxriizxpbvjg4dq89u/new_homepage_2-720p.mp4?rlkey=dl61odezz1wj1dqo2xi90r5zj&st=g8p1uz2p&raw=1",
  },
] satisfies HeroVideoSource[];

const loadingHeroVideos = fallbackPosters.map((poster) => ({ poster, src: "" })) satisfies HeroVideoSource[];

const heroPlaybackSrcByPreviewSrc: Record<string, string> = Object.fromEntries(
  defaultHeroVideos.map((video) => [video.src, video.playbackSrc ?? video.src]),
);

function getCmsMobilePreviewSrc(video: { mobile_video_url?: string | null }): string | undefined {
  return video.mobile_video_url || undefined;
}

function getHeroMobilePreviewSrc(video: HeroVideoSource | undefined): string | undefined {
  const src = video?.mobileSrc ?? video?.src;
  return src || undefined;
}

const offeringCards = [
  {
    title: "Mirrored, Step-by-Step Instruction",
    description:
      "Learn with clear, easy-to-follow guidance and built-in practice sessions—just like in a live class.",
    icon: OfferPracticeIcon,
  },
  {
    title: "Full Performance Video with Music",
    description:
      "Get studio-level rehearsal from anywhere with a complete mirrored routine for effortless follow-along.",
    icon: OfferPerformanceIcon,
  },
];

const processSteps = [
  {
    step: "Step One",
    title: "Start with the Dance Library",
    description:
      "Choose from a growing collection of dance tutorials. Each song is carefully timed so the ending flows effortlessly into the next. No need to worry about mismatched transitions—we’ve taken care of that for you.",
    icon: ProcessLibraryIcon,
  },
  {
    step: "Step Two",
    title: "Create Your Performance",
    description:
      "Once you've purchased your tutorials, use our \"Create My Performance\" tool to organize them into a custom set. Whether you're a solo performer or part of a group rehearsing remotely, this feature makes planning and preparation simple.",
    icon: ProcessPerformanceIcon,
  },
  {
    step: "Step Three",
    title: "Practice-Ready Sharing",
    description:
      "Send a view-only link to your group members for the dances they're part of, allowing everyone to practice at their own pace and arrive in sync.",
    icon: ProcessSharingIcon,
  },
];

const dancerTypes = [
  { label: "Signature Routine", cols: 1 },
  { label: "Low Movement (Accessible)", cols: 2 },
  { label: "Beginner", cols: 1 },
  { label: "Intermediate", cols: 1 },
  { label: "All-Male", cols: 1 },
  { label: "All-Female", cols: 1 },
  { label: "Coed", cols: 1 },
  { label: "Couples", cols: 1 },
  { label: "Groom", cols: 1 },
  { label: "Groomsmen", cols: 1 },
  { label: "Family of the Groom", cols: 2 },
  { label: "Bride", cols: 1 },
  { label: "Bridesmaids", cols: 1 },
  { label: "Family of the Bride", cols: 2 },
];

const testimonials = [
  {
    name: "Anjali M.",
    role: "Sangeet Dance",
    quote:
      "BollyRhythm made our family's sangeet performance so easy and fun! The flexibility to learn on our own time took away all the stress, and the end result was amazing. We got so many compliments!",
    stars: 5,
  },
  {
    name: "Rhea S.",
    role: "Reception dance",
    quote:
      "I hadn't danced in years, but BollyRhythm made it easy. The videos were easy to follow, and I actually looked forward to practicing!",
    stars: 5,
  },
  {
    name: "Neha & Friends",
    role: "Bridesmaid Dance",
    quote:
      "We were spread across three cities and still pulled off a coordinated performance thanks to BollyRhythm. It was such a special moment, and we couldn't have done it without this platform.",
    stars: 5,
  },
  {
    name: "Priya K.",
    role: "Family dance",
    quote:
      "I loved being able to learn at my own pace. No pressure and I felt so connected to my roots throughout the process. Truly grateful!",
    stars: 5,
  },
];

const testimonialCards = testimonials.map((testimonial, index) => ({
  ...testimonial,
  displayIndex: String(index + 1).padStart(2, "0"),
}));

const testimonialTracks = Array.from({ length: 3 }, (_, trackIndex) => ({
  key: `track-${trackIndex}`,
  cards: testimonialCards.map((testimonial) => ({
    ...testimonial,
    key: `${trackIndex}-${testimonial.name}`,
  })),
}));

type PricingPlanSection = {
  title: string;
  items: string[];
};

type PricingPlan = {
  key: "signature" | "build" | "sessions";
  title: string;
  badges: string[];
  description: string;
  displayPrice: string;
  displayPriceNote?: string;
  pricingHeading?: string;
  pricingSideNote?: string;
  pricingRows: Array<{
    label: string;
    value: string;
  }>;
  perfectFor: string[];
  topIncluded: string[];
  details: PricingPlanSection[];
  addOns?: Array<{
    productKey?: "custom_music_mix";
    title: string;
    description: string;
  }>;
  footerNote?: string;
  footerBadges?: string[];
  ctaLabel: string;
  href: string;
  featured?: boolean;
  orderClassName: string;
};

const centeredPricingBadges = new Set(["Best Value", "Most Flexible"]);

const pricingPlans: PricingPlan[] = [
  {
    key: "signature",
    title: "Signature Dance Routine",
    badges: ["Best Value"],
    description:
      "A complete, ready-to-perform routine - music, structure, and flow already designed for you.",
    displayPrice: "$149.00",
    displayPriceNote: "total",
    pricingHeading: "Pricing (Full Routine)",
    pricingRows: [{ label: "Full routine", value: "$149.00 total" }],
    perfectFor: [
      "Wedding party performances",
      "Couple dances",
      "Tight timelines",
      "Stress-free planning",
    ],
    topIncluded: [
      "Ready-made routine",
      "Built-in transitions",
      "Performance music mix",
      "Performance guide",
    ],
    details: [
      {
        title: "Learn & Practice",
        items: [
          "Professional, step-by-step instruction",
          "Mirrored tutorials with guided practice sessions",
          "Adjust playback speed to practice at your own pace",
          "Full mirrored performance video with music",
          "Free Formations Guide included in our tutorial library",
        ],
      },
      {
        title: "Access & Flexibility",
        items: [
          "4 months of access",
          "Weekly extensions available ($10/week)",
          "Shareable practice link for each tutorial",
          "Practice anywhere, anytime on desktop, mobile, or AirPlay",
          "Expand your routine with more tutorials, or upgrade anytime with add-ons",
        ],
      },
      {
        title: "Create My Performance Tool",
        items: [
          "Add performance notes (formations, roles, transitions, and more)",
          "Upload and share the full music mix (MP3) with your group",
          "Share a complete performance link",
          "Built-in group chat to stay organized and motivated",
        ],
      },
    ],
    ctaLabel: "Browse Signature Routines",
    href: "/dance-library?tags=Signature%20Routine",
    featured: true,
    orderClassName: "order-1 md:col-span-2 xl:order-2 xl:col-span-1",
  },
  {
    key: "build",
    title: "Build Your Own Dance Routine",
    badges: ["Most Flexible"],
    description: "Pick your favorite songs and create a routine that's uniquely yours.",
    displayPrice: "$35.00",
    displayPriceNote: "each",
    pricingHeading: "Pricing (2+ Tutorials)",
    pricingRows: [
      { label: "1 tutorial", value: "$45" },
      { label: "2+ tutorials", value: "$35 each" },
    ],
    perfectFor: [
      "Song-specific routines",
      "More creative control",
      "Mix-and-match styles",
    ],
    topIncluded: [
      "Pick any tutorials",
      "Set your own order",
    ],
    details: [
      {
        title: "Learn & Practice",
        items: [
          "Professional, step-by-step instruction",
          "Mirrored tutorials with guided practice sessions",
          "Adjust playback speed to practice at your own pace",
          "Full mirrored performance video with music",
          "Free Formations Guide included in our tutorial library",
        ],
      },
      {
        title: "Access & Flexibility",
        items: [
          "4 months of access",
          "Weekly extensions available ($10/week)",
          "Shareable practice link for each tutorial",
          "Practice anywhere, anytime on desktop, mobile, or AirPlay",
          "Expand your routine with more tutorials, or upgrade anytime with add-ons",
        ],
      },
      {
        title: "Create My Performance Tool",
        items: [
          "Arrange your tutorials in any order to customize your routine flow",
          "Add performance notes (formations, roles, transitions, and more)",
          "Upload and share the full music mix (MP3) with your group",
          "Share a complete performance link",
          "Built-in group chat to stay organized and motivated",
        ],
      },
    ],
    addOns: [
      {
        productKey: "custom_music_mix",
        title: "Custom Music Mix (+$59)",
        description: "We mix your chosen songs into one clean performance track for your DJ.",
      },
    ],
    footerNote: "Most routines are 3-4 songs. Bundle and save!",
    ctaLabel: "Start Building Your Routine",
    href: "/dance-library",
    orderClassName: "order-2 xl:order-3",
  },
  {
    key: "sessions",
    title: "Dance Session",
    badges: ["Fun", "Fitness", "Skill Building"],
    description: "Learn a full Bollywood dance - perfect for fun, workouts, or leveling up your moves.",
    displayPrice: "$45.00",
    displayPriceNote: "each",
    pricingHeading: "Pricing (Single Tutorial)",
    pricingRows: [{ label: "Single tutorial", value: "$45.00 each" }],
    perfectFor: [
      "Fun dancing",
      "Quick cardio",
      "Learning Bollywood basics",
      "First-time BollyRhythm users",
    ],
    topIncluded: [
      "Step-by-step teaching",
      "Mirrored guided practice",
      "Practice anywhere, on any device",
      "Share with friends for free",
    ],
    details: [
      {
        title: "More Included",
        items: [
          "Adjust playback speed to practice at your own pace",
          "Full mirrored performance video with music",
          "4 months of access",
          "Weekly extensions available ($10/week)",
          "Add more tutorials to your library anytime",
          "Shareable practice link for each tutorial",
        ],
      },
    ],
    ctaLabel: "Explore Dance Sessions",
    href: "/dance-library",
    orderClassName: "order-3 xl:order-1",
  },
];

const pricingPracticeAddOn = {
  title: "Virtual Dance Practice",
  description:
    "Practice live with one of our instructors, get feedback, formation guidance, and build confidence - whether you're preparing for a performance or just improving your skills.",
  price: `$${DEFAULT_VIRTUAL_PRACTICE_ADD_ON.price}`,
  bullets: [
    "60-minute guided session",
    "Personalized feedback and performance recommendations",
    "Perfect for groups or beginners",
  ],
  ctaLabel: "Add to Cart",
};

const offerPreviewVideo = {
  poster:
    "https://cdn.prod.website-files.com/686229256a807206c1874545%2F68a170d56575ce25737b09b1_bollyrhythm_-_how_it_works%20%28720p%29-poster-00001.jpg",
  mp4: "https://cdn.prod.website-files.com/686229256a807206c1874545%2F68a170d56575ce25737b09b1_bollyrhythm_-_how_it_works%20%28720p%29-transcode.mp4",
  webm:
    "https://cdn.prod.website-files.com/686229256a807206c1874545%2F68a170d56575ce25737b09b1_bollyrhythm_-_how_it_works%20%28720p%29-transcode.webm",
  iframeSrc:
    "https://player.vimeo.com/video/1105492316?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1",
};


function HeroArc() {
  return (
    <svg width="100%" height="100%" viewBox="0 0 1024 459" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_hero)">
        <mask id="path-1-inside-hero" fill="white">
          <path d="M1024 532C1024 396.209 970.057 265.98 874.039 169.961C778.02 73.9427 647.791 20 512 20C376.209 20 245.98 73.9427 149.961 169.961C53.9427 265.98 2.05039e-05 396.209 0 532L512 532H1024Z" />
        </mask>
        <path d="M1024 532C1024 396.209 970.057 265.98 874.039 169.961C778.02 73.9427 647.791 20 512 20C376.209 20 245.98 73.9427 149.961 169.961C53.9427 265.98 2.05039e-05 396.209 0 532L512 532H1024Z" stroke="url(#paint0_hero)" strokeWidth="2" mask="url(#path-1-inside-hero)" />
      </g>
      <defs>
        <linearGradient id="paint0_hero" x1="512" y1="20" x2="512" y2="1044" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FFA724" />
          <stop offset="0.480475" stopColor="#FFA724" stopOpacity="0" />
        </linearGradient>
        <clipPath id="clip0_hero">
          <rect width="1024" height="459" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

function HeroArrowLeft() {
  return (
    <svg width="100%" viewBox="0 0 45 108" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19.2938 60.5039C21.6065 60.8803 23.9636 60.8951 26.2809 60.548C28.5806 60.1683 30.8164 59.4706 32.9239 58.4748C34.9995 57.5368 36.9544 56.3519 38.7464 54.946C40.5441 53.5492 42.082 51.8471 43.2898 49.9174C43.8947 48.942 44.3553 47.8841 44.6572 46.7768C44.9859 45.6303 45.0582 44.4255 44.869 43.2479C44.6431 41.9359 43.9847 40.7372 42.9987 39.8426C41.9662 38.9536 40.7029 38.3755 39.3552 38.1753C38.7286 38.0677 38.0942 38.0117 37.4584 38.0076C36.8506 38.0229 36.244 38.0701 35.6411 38.1488C35.061 38.2231 34.4863 38.3351 33.9208 38.484L33.0386 38.6869L32.2269 38.9428C30.1726 39.6311 28.1901 40.5174 26.3073 41.5894C24.4479 42.6143 22.7431 43.8975 21.2435 45.4005C19.8225 46.8274 18.616 48.4529 17.6617 50.2262L17.3088 50.8614L17.0177 51.523C16.8236 51.9553 16.6207 52.4052 16.4443 52.8287C16.1047 53.6951 15.8102 54.5785 15.5621 55.4753C15.4209 55.9958 15.3062 56.5252 15.1916 57.0545C14.724 56.878 14.3093 56.6839 13.8153 56.481C12.0264 55.6616 10.3558 54.6052 8.8485 53.3404C7.42104 52.111 6.18944 50.6712 5.19616 49.0705C3.39276 45.955 2.31898 42.4712 2.05551 38.881C1.81108 35.5167 2.03674 32.1347 2.72598 28.8327C3.34691 25.8066 4.21135 22.8357 5.31085 19.9489C6.24753 17.4079 7.35275 14.9322 8.61912 12.5384C10.4575 9.01915 12.8595 5.82451 15.7297 3.08111C17.8293 1.14908 19.2585 0.302154 19.1262 0.196289C18.7413 0.305393 18.3775 0.478359 18.0499 0.70797C17.0784 1.28006 16.1523 1.92597 15.2798 2.64C12.1798 5.26694 9.57198 8.42444 7.57812 11.9649C6.19288 14.3657 4.97547 16.8595 3.93461 19.4284C2.7191 22.3581 1.74542 25.3824 1.02333 28.471C0.21449 31.9083 -0.103321 35.4428 0.0793671 38.9692C0.274641 42.8613 1.36444 46.656 3.26413 50.0586C4.34075 51.8756 5.69177 53.5153 7.26935 54.9195C8.93287 56.3674 10.7883 57.5786 12.7831 58.5189C13.4658 58.8525 14.1668 59.147 14.8828 59.4011C14.6197 61.4632 14.543 63.5448 14.6534 65.6207C14.9941 71.1412 16.1475 76.5813 18.0764 81.765C19.4478 85.5312 21.1206 89.1806 23.0785 92.678C25.2179 96.5697 27.7705 100.22 30.6919 103.564L31.6447 104.57L30.3743 104.2C28.9981 103.838 27.6483 103.564 26.3603 103.317C24.0757 102.912 21.7634 102.682 19.4438 102.629C15.4739 102.541 13.0654 103.017 13.0831 103.335C13.1007 103.653 15.5003 103.838 19.2585 104.341C21.1464 104.614 23.3784 104.958 25.8133 105.514C27.0396 105.788 28.31 106.105 29.6068 106.476C30.2596 106.67 30.9213 106.864 31.5829 107.076C31.9182 107.181 32.2093 107.296 32.7122 107.42C33.3658 107.598 34.0597 107.558 34.6883 107.305C35.0216 107.162 35.3219 106.951 35.5705 106.687C35.7621 106.477 35.9253 106.243 36.0557 105.99C36.2386 105.613 36.3806 105.216 36.4792 104.808C36.6556 104.164 36.8144 103.52 36.982 102.894C37.2996 101.632 37.5908 100.415 37.8642 99.2504C38.3759 96.9213 38.7464 94.8393 39.0287 93.022C39.5581 89.4932 39.6286 87.2259 39.2758 87.1642C38.9229 87.1024 38.1907 89.2373 37.1849 92.6868L35.4823 98.7475C35.1647 99.8885 34.8295 101.079 34.4766 102.32C34.3001 102.938 34.1237 103.573 33.9472 104.208C33.9434 104.226 33.9434 104.244 33.9472 104.261C33.6561 103.803 33.2238 103.167 32.571 102.285C30.063 98.8273 27.785 95.2083 25.7516 91.4517C23.95 88.0389 22.3962 84.5009 21.1023 80.8652C19.307 75.9246 18.2286 70.7524 17.8999 65.506C17.8145 63.7697 17.8646 62.0294 18.0499 60.301C18.4645 60.3892 18.8792 60.4774 19.2938 60.548V60.5039ZM18.791 56.1987C18.9994 55.401 19.2497 54.6147 19.5408 53.8432C19.6908 53.4639 19.8672 53.0845 20.0349 52.6964L20.2907 52.1229L20.5995 51.5671C21.4023 50.0338 22.4313 48.6301 23.6519 47.4031C24.9373 46.097 26.4016 44.9802 28.0012 44.086C29.6811 43.1044 31.4542 42.2918 33.2944 41.66L33.9914 41.4306L34.6883 41.2542C35.1474 41.1347 35.6128 41.0405 36.0822 40.9719C36.5498 40.8836 36.9644 40.8925 37.4673 40.8483C37.9047 40.8471 38.3414 40.8825 38.7729 40.9542C39.5261 41.0069 40.2407 41.3071 40.8056 41.8081C41.3704 42.3091 41.7538 42.9828 41.8959 43.7243C42.0773 45.4586 41.65 47.202 40.6873 48.6559C39.6697 50.3145 38.3711 51.7832 36.8497 52.9963C35.2503 54.2772 33.5027 55.3612 31.6447 56.2252C29.8001 57.1333 27.8372 57.7777 25.8133 58.1396C23.7878 58.4571 21.7251 58.4571 19.6996 58.1396C19.2585 58.0778 18.8174 57.9984 18.3851 57.9102C18.4998 57.328 18.6233 56.7545 18.7733 56.1458" fill="currentColor" />
    </svg>
  );
}

function HeroArrowRight() {
  return (
    <svg width="100%" viewBox="0 0 45 108" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M25.7062 60.5039C23.3935 60.8803 21.0364 60.8951 18.7191 60.548C16.4194 60.1683 14.1836 59.4706 12.0761 58.4748C10.0005 57.5368 8.04557 56.3519 6.25355 54.946C4.45591 53.5492 2.91802 51.8471 1.71019 49.9174C1.10532 48.942 0.644745 47.8841 0.342773 46.7768C0.0140572 45.6303 -0.0582352 44.4255 0.131039 43.2479C0.356888 41.9359 1.01527 40.7372 2.00132 39.8426C3.03382 38.9536 4.29714 38.3755 5.64482 38.1753C6.27144 38.0677 6.90582 38.0117 7.54158 38.0076C8.14941 38.0229 8.75602 38.0701 9.35892 38.1488C9.93902 38.2231 10.5137 38.3351 11.0792 38.484L11.9614 38.6869L12.7731 38.9428C14.8274 39.6311 16.8099 40.5174 18.6927 41.5894C20.5521 42.6143 22.2569 43.8975 23.7565 45.4005C25.1775 46.8274 26.384 48.4529 27.3383 50.2262L27.6912 50.8614L27.9823 51.523C28.1764 51.9553 28.3793 52.4052 28.5557 52.8287C28.8953 53.6951 29.1898 54.5785 29.4379 55.4753C29.5791 55.9958 29.6938 56.5252 29.8084 57.0545C30.276 56.878 30.6907 56.6839 31.1847 56.481C32.9736 55.6616 34.6442 54.6052 36.1515 53.3404C37.579 52.111 38.8106 50.6712 39.8038 49.0705C41.6072 45.955 42.681 42.4712 42.9445 38.881C43.1889 35.5167 42.9633 32.1347 42.274 28.8327C41.6531 25.8066 40.7887 22.8357 39.6892 19.9489C38.7525 17.4079 37.6473 14.9322 36.3809 12.5384C34.5425 9.01915 32.1405 5.82451 29.2703 3.08111C27.1707 1.14908 25.7415 0.302154 25.8738 0.196289C26.2587 0.305393 26.6225 0.478359 26.9501 0.70797C27.9216 1.28006 28.8477 1.92597 29.7202 2.64C32.8202 5.26694 35.428 8.42444 37.4219 11.9649C38.8071 14.3657 40.0245 16.8595 41.0654 19.4284C42.2809 22.3581 43.2546 25.3824 43.9767 28.471C44.7855 31.9083 45.1033 35.4428 44.9206 38.9692C44.7254 42.8613 43.6356 46.656 41.7359 50.0586C40.6593 51.8756 39.3082 53.5153 37.7307 54.9195C36.0671 56.3674 34.2117 57.5786 32.2169 58.5189C31.5342 58.8525 30.8332 59.147 30.1172 59.4011C30.3803 61.4632 30.457 63.5448 30.3466 65.6207C30.0059 71.1412 28.8525 76.5813 26.9236 81.765C25.5522 85.5312 23.8794 89.1806 21.9215 92.678C19.7821 96.5697 17.2295 100.22 14.3081 103.564L13.3553 104.57L14.6257 104.2C16.0019 103.838 17.3517 103.564 18.6397 103.317C20.9243 102.912 23.2366 102.682 25.5562 102.629C29.5261 102.541 31.9346 103.017 31.9169 103.335C31.8993 103.653 29.4997 103.838 25.7415 104.341C23.8536 104.614 21.6216 104.958 19.1867 105.514C17.9604 105.788 16.69 106.105 15.3932 106.476C14.7404 106.67 14.0787 106.864 13.4171 107.076C13.0818 107.181 12.7907 107.296 12.2878 107.42C11.6342 107.598 10.9403 107.558 10.3117 107.305C9.97842 107.162 9.67813 106.951 9.4295 106.687C9.23786 106.477 9.07472 106.243 8.94428 105.99C8.76136 105.613 8.61941 105.216 8.52082 104.808C8.34438 104.164 8.18558 103.52 8.01796 102.894C7.70037 101.632 7.40924 100.415 7.13576 99.2504C6.62408 96.9213 6.25356 94.8393 5.97125 93.022C5.44193 89.4932 5.37135 87.2259 5.72424 87.1642C6.07712 87.1024 6.80934 89.2373 7.81506 92.6868L9.51771 98.7475C9.8353 99.8885 10.1705 101.079 10.5234 102.32C10.6999 102.938 10.8763 103.573 11.0528 104.208C11.0566 104.226 11.0566 104.244 11.0528 104.261C11.3439 103.803 11.7762 103.167 12.429 102.285C14.937 98.8273 17.215 95.2083 19.2484 91.4517C21.05 88.0389 22.6038 84.5009 23.8977 80.8652C25.693 75.9246 26.7714 70.7524 27.1001 65.506C27.1855 63.7697 27.1354 62.0294 26.9501 60.301C26.5355 60.3892 26.1208 60.4774 25.7062 60.548V60.5039ZM26.209 56.1987C26.0006 55.401 25.7503 54.6147 25.4592 53.8432C25.3092 53.4639 25.1328 53.0845 24.9651 52.6964L24.7093 52.1229L24.4005 51.5671C23.5977 50.0338 22.5687 48.6301 21.3481 47.4031C20.0627 46.097 18.5984 44.9802 16.9988 44.086C15.3189 43.1044 13.5458 42.2918 11.7056 41.66L11.0086 41.4306L10.3117 41.2542C9.85257 41.1347 9.38725 41.0405 8.91782 40.9719C8.45024 40.8836 8.0356 40.8925 7.53275 40.8483C7.09533 40.8471 6.65858 40.8825 6.22709 40.9542C5.4739 41.0069 4.75925 41.3071 4.19442 41.8081C3.62957 42.3091 3.24624 42.9828 3.10408 43.7243C2.92273 45.4586 3.34999 47.202 4.3127 48.6559C5.33026 50.3145 6.62886 51.7832 8.1503 52.9963C9.74974 54.2772 11.4973 55.3612 13.3553 56.2252C15.1999 57.1333 17.1628 57.7777 19.1867 58.1396C21.2122 58.4571 23.2749 58.4571 25.3004 58.1396C25.7415 58.0778 26.1826 57.9984 26.6149 57.9102C26.5002 57.328 26.3767 56.7545 26.2267 56.1458" fill="currentColor" />
    </svg>
  );
}

// Track which video is currently playing so only one plays at a time
let currentlyPlayingVideo: HTMLVideoElement | null = null;

type FullscreenCapableElement = HTMLDivElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

type FullscreenDocument = Document & {
  webkitExitFullscreen?: () => Promise<void> | void;
  webkitFullscreenElement?: Element | null;
};

type FullscreenVideoElement = HTMLVideoElement & {
  webkitDisplayingFullscreen?: boolean;
  webkitEnterFullscreen?: () => void;
};

function formatVideoClock(totalSeconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function HeroVideoCard({
  video,
  isVisible = true,
  loadPreview = true,
  previewSrc,
  previewPreload = "metadata",
  previewAutoPlay = true,
  showPlayOverlay = true,
  onPreviewReady,
  className,
  playButtonClassName,
  playIconClassName,
}: {
  video: HeroVideoSource;
  isVisible?: boolean;
  loadPreview?: boolean;
  previewSrc?: string;
  previewPreload?: "auto" | "metadata" | "none";
  previewAutoPlay?: boolean;
  showPlayOverlay?: boolean;
  onPreviewReady?: () => void;
  className?: string;
  playButtonClassName?: string;
  playIconClassName?: string;
}) {
  const fullscreenHostRef = useRef<HTMLDivElement>(null);
  const activeVideoRef = useRef<HTMLVideoElement>(null);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const previewReadyReportedRef = useRef(false);
  const previewWasAutoPlayingRef = useRef(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPreviewFrameReady, setIsPreviewFrameReady] = useState(false);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const previewVideoSrc = previewSrc ?? video.src;
  const shouldShowPreviewVideo = isPreviewFrameReady && isVisible && isPreviewPlaying && !isPlaying;

  useEffect(() => {
    previewReadyReportedRef.current = false;
    setIsPreviewFrameReady(false);
    setIsPreviewPlaying(false);
  }, [loadPreview, previewVideoSrc]);

  useEffect(() => {
    if (!loadPreview || !previewVideoSrc) {
      setIsPreviewPlaying(false);
      return;
    }

    const previewVideo = previewVideoRef.current;
    if (!previewVideo) {
      return;
    }

    let cancelled = false;

    previewVideo.controls = false;
    previewVideo.muted = true;
    previewVideo.defaultMuted = true;
    previewVideo.setAttribute("playsinline", "");
    previewVideo.setAttribute("webkit-playsinline", "");
    previewVideo.load();

    if (!previewAutoPlay) {
      if (previewWasAutoPlayingRef.current) {
        previewVideo.pause();
      }
      setIsPreviewPlaying(false);
      previewWasAutoPlayingRef.current = false;
      return () => {
        cancelled = true;
      };
    }

    previewWasAutoPlayingRef.current = true;
    const playPromise = previewVideo.play();
    if (playPromise && typeof playPromise.then === "function") {
      void playPromise
        .then(() => {
          if (cancelled || previewVideo.paused) {
            return;
          }

          setIsPreviewPlaying(true);
          setIsPreviewFrameReady(true);

          if (!previewReadyReportedRef.current) {
            previewReadyReportedRef.current = true;
            onPreviewReady?.();
          }
        })
        .catch(() => {
          if (cancelled) {
            return;
          }

          setIsPreviewPlaying(false);
          setIsPreviewFrameReady(false);
          // Muted autoplay can still be blocked on some mobile modes; the poster/play button remains available.
        });
    }

    return () => {
      cancelled = true;
    };
  }, [loadPreview, onPreviewReady, previewAutoPlay, previewVideoSrc]);

  const handlePreviewReady = useCallback(() => {
    setIsPreviewFrameReady(true);

    if (previewReadyReportedRef.current) {
      return;
    }

    previewReadyReportedRef.current = true;
    onPreviewReady?.();
  }, [onPreviewReady]);

  const handlePreviewPlaying = useCallback(() => {
    setIsPreviewPlaying(true);
    handlePreviewReady();
  }, [handlePreviewReady]);

  const handlePreviewPaused = useCallback(() => {
    setIsPreviewPlaying(false);
  }, []);

  const handlePreviewError = useCallback(() => {
    setIsPreviewFrameReady(false);
    setIsPreviewPlaying(false);

    if (previewReadyReportedRef.current) {
      return;
    }

    previewReadyReportedRef.current = true;
    onPreviewReady?.();
  }, [onPreviewReady]);

  const stopPlaying = useCallback((resetToStart = true) => {
    const vid = activeVideoRef.current;
    if (vid) {
      vid.pause();
      if (resetToStart) {
        vid.currentTime = 0;
      }
    }
    setIsPlaying(false);
    setIsPaused(false);
    if (resetToStart) {
      setCurrentTime(0);
    }
    if (currentlyPlayingVideo === activeVideoRef.current) currentlyPlayingVideo = null;
  }, []);

  const handlePlayClick = useCallback(() => {
    // Stop any other playing video
    if (currentlyPlayingVideo) {
      currentlyPlayingVideo.pause();
      currentlyPlayingVideo.dispatchEvent(new Event("externalStop"));
    }

    setIsPlaying(true);
    requestAnimationFrame(() => {
      const vid = activeVideoRef.current;
      if (vid) {
        const playPromise = vid.play();
        currentlyPlayingVideo = vid;
        if (playPromise !== undefined) {
          void playPromise.catch(() => {
            if (currentlyPlayingVideo === vid) currentlyPlayingVideo = null;
            setIsPlaying(false);
          });
        }
      }
    });
  }, []);

  const togglePlayback = useCallback(() => {
    const vid = activeVideoRef.current;
    if (!vid) return;

    if (vid.paused) {
      const playPromise = vid.play();
      currentlyPlayingVideo = vid;
      if (playPromise !== undefined) {
        void playPromise.catch(() => {
          if (currentlyPlayingVideo === vid) currentlyPlayingVideo = null;
          setIsPaused(true);
        });
      }
      return;
    }

    vid.pause();
    if (currentlyPlayingVideo === vid) currentlyPlayingVideo = null;
  }, []);

  const toggleMute = useCallback(() => {
    const vid = activeVideoRef.current;
    if (!vid) return;

    const nextMuted = !vid.muted;
    vid.muted = nextMuted;
    setIsMuted(nextMuted);
  }, []);

  const handleSeekChange = useCallback((nextValue: number) => {
    const vid = activeVideoRef.current;
    if (!vid) return;

    vid.currentTime = nextValue;
    setCurrentTime(nextValue);
  }, []);

  const exitFullscreen = useCallback(async () => {
    const doc = document as FullscreenDocument;

    try {
      if (doc.fullscreenElement) {
        await doc.exitFullscreen?.();
        return;
      }

      if (doc.webkitFullscreenElement) {
        await doc.webkitExitFullscreen?.();
      }
    } catch {
      // Ignore unsupported fullscreen exits.
    }
  }, []);

  const toggleFullscreen = useCallback(async () => {
    const doc = document as FullscreenDocument;
    const host = fullscreenHostRef.current as FullscreenCapableElement | null;
    const vid = activeVideoRef.current as FullscreenVideoElement | null;

    if (!host) return;

    try {
      if (doc.fullscreenElement === host || doc.webkitFullscreenElement === host) {
        await exitFullscreen();
        return;
      }

      if (host.requestFullscreen) {
        await host.requestFullscreen();
        return;
      }

      if (host.webkitRequestFullscreen) {
        await host.webkitRequestFullscreen();
        return;
      }

      vid?.webkitEnterFullscreen?.();
    } catch {
      // Ignore unsupported fullscreen requests.
    }
  }, [exitFullscreen]);

  useEffect(() => {
    const vid = activeVideoRef.current;
    if (!vid) return;

    const handleEnded = () => stopPlaying();
    const handleExternalStop = () => stopPlaying(false);
    const handleLoadedMetadata = () => {
      setDuration(Number.isFinite(vid.duration) ? vid.duration : 0);
      setCurrentTime(vid.currentTime);
      setIsMuted(vid.muted);
    };
    const handleDurationChange = () => {
      setDuration(Number.isFinite(vid.duration) ? vid.duration : 0);
    };
    const handleTimeUpdate = () => setCurrentTime(vid.currentTime);
    const handlePlay = () => {
      setIsPaused(false);
      setIsMuted(vid.muted);
    };
    const handlePause = () => {
      if (!vid.ended) {
        setIsPaused(true);
      }
    };
    const handleVolumeChange = () => setIsMuted(vid.muted);

    vid.addEventListener("ended", handleEnded);
    vid.addEventListener("externalStop", handleExternalStop);
    vid.addEventListener("loadedmetadata", handleLoadedMetadata);
    vid.addEventListener("durationchange", handleDurationChange);
    vid.addEventListener("timeupdate", handleTimeUpdate);
    vid.addEventListener("play", handlePlay);
    vid.addEventListener("pause", handlePause);
    vid.addEventListener("volumechange", handleVolumeChange);

    handleLoadedMetadata();

    return () => {
      vid.removeEventListener("ended", handleEnded);
      vid.removeEventListener("externalStop", handleExternalStop);
      vid.removeEventListener("loadedmetadata", handleLoadedMetadata);
      vid.removeEventListener("durationchange", handleDurationChange);
      vid.removeEventListener("timeupdate", handleTimeUpdate);
      vid.removeEventListener("play", handlePlay);
      vid.removeEventListener("pause", handlePause);
      vid.removeEventListener("volumechange", handleVolumeChange);
    };
  }, [stopPlaying]);

  useEffect(() => {
    const doc = document as FullscreenDocument;
    const vid = activeVideoRef.current as FullscreenVideoElement | null;

    const syncFullscreenState = () => {
      const host = fullscreenHostRef.current;
      setIsFullscreen(Boolean(
        (host && (doc.fullscreenElement === host || doc.webkitFullscreenElement === host)) ||
        vid?.webkitDisplayingFullscreen
      ));
    };

    syncFullscreenState();
    document.addEventListener("fullscreenchange", syncFullscreenState);
    document.addEventListener("webkitfullscreenchange", syncFullscreenState);
    vid?.addEventListener("webkitbeginfullscreen", syncFullscreenState);
    vid?.addEventListener("webkitendfullscreen", syncFullscreenState);

    return () => {
      document.removeEventListener("fullscreenchange", syncFullscreenState);
      document.removeEventListener("webkitfullscreenchange", syncFullscreenState);
      vid?.removeEventListener("webkitbeginfullscreen", syncFullscreenState);
      vid?.removeEventListener("webkitendfullscreen", syncFullscreenState);
    };
  }, [isPlaying]);

  useEffect(() => {
    if (isVisible || !isPlaying) {
      return;
    }

    stopPlaying(false);
  }, [isPlaying, isVisible, stopPlaying]);

  return (
    <div
      ref={fullscreenHostRef}
      className={cn(
        "relative isolate flex shrink-0 items-center justify-center overflow-hidden rounded-[1.45rem] bg-[#e8e4e7] before:pointer-events-none before:absolute before:inset-0 before:z-[3] before:rounded-[inherit] before:shadow-[inset_0_0_0_3px_#ffffff]",
        isFullscreen && "h-full w-full rounded-none before:shadow-none",
        className,
      )}
    >
      <img
        src={video.poster}
        alt=""
        className={cn(
          "absolute inset-0 z-[1] h-full w-full rounded-[inherit] object-cover transition-opacity duration-200",
          isFullscreen ? "[clip-path:none]" : "[clip-path:inset(0_round_1.45rem)]",
          isPlaying || shouldShowPreviewVideo ? "opacity-0" : "opacity-100"
        )}
        aria-hidden="true"
      />
      {previewVideoSrc && loadPreview ? (
        <video
          ref={previewVideoRef}
          src={previewVideoSrc}
          className={cn(
            "hero-preview-video pointer-events-none absolute inset-0 z-[1] h-full w-full rounded-[inherit] object-cover transition-opacity duration-200",
            isFullscreen ? "[clip-path:none]" : "[clip-path:inset(0_round_1.45rem)]",
            shouldShowPreviewVideo ? "visible opacity-100" : isVisible ? "visible opacity-0" : "invisible opacity-0"
          )}
          autoPlay={previewAutoPlay}
          muted
          loop
          playsInline
          preload={previewPreload}
          onLoadedData={handlePreviewReady}
          onCanPlay={handlePreviewReady}
          onPlay={handlePreviewPlaying}
          onPlaying={handlePreviewPlaying}
          onPause={handlePreviewPaused}
          onError={handlePreviewError}
          disablePictureInPicture
          disableRemotePlayback
          aria-hidden="true"
        />
      ) : null}
      <video
        ref={activeVideoRef}
        src={isPlaying ? (video.playbackSrc ?? video.src) : undefined}
        poster={isPlaying ? video.poster : undefined}
        className={cn(
          "hero-video-player absolute inset-0 z-[2] h-full w-full rounded-[inherit] object-cover transition-opacity duration-200",
          isFullscreen ? "[clip-path:none]" : "[clip-path:inset(0_round_1.45rem)]",
          isPlaying ? "opacity-100" : "pointer-events-none opacity-0"
        )}
        onClick={togglePlayback}
        playsInline
        preload="none"
        disablePictureInPicture
        disableRemotePlayback
      />
      {isPlaying && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[3] bg-gradient-to-t from-[rgb(16_9_15_/_42%)] via-[rgb(16_9_15_/_18%)] to-transparent px-3 pb-3 pt-10 max-[479px]:px-2.5 max-[479px]:pb-2.5">
          <div className="pointer-events-auto flex items-center gap-2.5 rounded-full bg-[linear-gradient(180deg,rgba(77,36,63,0.62)_0%,rgba(41,19,34,0.78)_100%)] px-3 py-2 text-white backdrop-blur-[14px] max-[479px]:gap-2 max-[479px]:px-2.5 max-[479px]:py-1.5">
            <button
              type="button"
              onClick={togglePlayback}
              aria-label={isPaused ? "Resume preview" : "Pause preview"}
              className="inline-flex h-8.5 w-8.5 shrink-0 items-center justify-center rounded-full bg-white/8 transition-colors hover:bg-white/14 max-[479px]:h-8 max-[479px]:w-8"
            >
              {isPaused ? <Play className="h-3.5 w-3.5 fill-current" /> : <Pause className="h-3.5 w-3.5" />}
            </button>

            <div
              className="hero-video-progress relative h-5 min-w-0 flex-1"
              style={{ ["--hero-video-progress" as string]: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
            >
              <div className="hero-video-progress__track" aria-hidden="true">
                <div className="hero-video-progress__fill" />
                <div className="hero-video-progress__thumb" />
              </div>
              <input
                type="range"
                min={0}
                max={Math.max(duration, 0)}
                step={0.1}
                value={Math.min(currentTime, duration || 0)}
                onChange={(event) => handleSeekChange(Number(event.target.value))}
                className="hero-video-progress__input"
                aria-label="Video progress"
              />
            </div>

            <div className="min-w-[3.35rem] shrink-0 text-right font-[family-name:Inter,sans-serif] text-[0.8rem] font-medium tabular-nums text-white/88 max-[479px]:min-w-[2.8rem] max-[479px]:text-[0.72rem]">
              -{formatVideoClock(Math.max(0, duration - currentTime))}
            </div>

            <button
              type="button"
              onClick={toggleMute}
              aria-label={isMuted ? "Unmute preview" : "Mute preview"}
              className="inline-flex h-8.5 w-8.5 shrink-0 items-center justify-center rounded-full bg-white/8 transition-colors hover:bg-white/14 max-[479px]:h-8 max-[479px]:w-8"
            >
              {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>

            <button
              type="button"
              onClick={() => void toggleFullscreen()}
              aria-label={isFullscreen ? "Exit fullscreen preview" : "Enter fullscreen preview"}
              className="inline-flex h-8.5 w-8.5 shrink-0 items-center justify-center rounded-full bg-white/8 transition-colors hover:bg-white/14 max-[479px]:hidden"
            >
              {isFullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>
      )}
      {/* Cover overlay with play button */}
      {showPlayOverlay && !isPlaying && (
        <div
          className="absolute inset-0 z-[2] flex cursor-pointer items-center justify-center overflow-hidden rounded-[inherit] [transform:translateZ(0)]"
          onClick={handlePlayClick}
        >
          <button
            type="button"
            aria-label="Play tutorial preview"
            className={cn(
              "absolute z-[1] flex aspect-square items-center justify-center rounded-full bg-[color-mix(in_srgb,#ffffff_60%,transparent)] text-white backdrop-blur-[5px]",
              playButtonClassName ?? "w-20 max-[767px]:w-16",
            )}
          >
            <span className={cn("flex", playIconClassName ?? "w-8 max-[767px]:w-6")}>
              <Play className="h-full w-full fill-current" />
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

function OfferEyebrowIcon() {
  return (
    <svg width="100%" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        opacity="0.2"
        d="M14.1981 11.1472L10.3246 12.5746L8.89724 16.4481C8.85782 16.5549 8.78661 16.6471 8.6932 16.7122C8.59979 16.7772 8.48867 16.8121 8.37482 16.8121C8.26097 16.8121 8.14986 16.7772 8.05645 16.7122C7.96304 16.6471 7.89182 16.5549 7.8524 16.4481L6.42506 12.5746L2.55154 11.1472C2.44473 11.1078 2.35258 11.0366 2.28749 10.9432C2.2224 10.8498 2.1875 10.7387 2.1875 10.6248C2.1875 10.511 2.2224 10.3999 2.28749 10.3064C2.35258 10.213 2.44473 10.1418 2.55154 10.1024L6.42506 8.67506L7.8524 4.80154C7.89182 4.69473 7.96304 4.60258 8.05645 4.53749C8.14986 4.4724 8.26097 4.4375 8.37482 4.4375C8.48867 4.4375 8.59979 4.4724 8.6932 4.53749C8.78661 4.60258 8.85782 4.69473 8.89724 4.80154L10.3246 8.67506L14.1981 10.1024C14.3049 10.1418 14.3971 10.213 14.4622 10.3064C14.5272 10.3999 14.5621 10.511 14.5621 10.6248C14.5621 10.7387 14.5272 10.8498 14.4622 10.9432C14.3971 11.0366 14.3049 11.1078 14.1981 11.1472Z"
        fill="currentColor"
      />
      <path
        d="M14.3952 9.57453L10.7685 8.23437L9.43255 4.60484C9.35343 4.38992 9.2103 4.20443 9.02245 4.07341C8.83461 3.94239 8.6111 3.87214 8.38208 3.87214C8.15305 3.87214 7.92954 3.94239 7.7417 4.07341C7.55386 4.20443 7.41072 4.38992 7.33161 4.60484L5.98723 8.23437L2.3577 9.57031C2.14278 9.64942 1.95729 9.79256 1.82627 9.9804C1.69525 10.1682 1.625 10.3918 1.625 10.6208C1.625 10.8498 1.69525 11.0733 1.82627 11.2612C1.95729 11.449 2.14278 11.5921 2.3577 11.6712L5.98723 13.0156L7.32317 16.6452C7.40228 16.8601 7.54542 17.0456 7.73326 17.1766C7.9211 17.3076 8.14462 17.3779 8.37364 17.3779C8.60266 17.3779 8.82617 17.3076 9.01402 17.1766C9.20186 17.0456 9.34499 16.8601 9.42411 16.6452L10.7685 13.0156L14.398 11.6797C14.6129 11.6006 14.7984 11.4574 14.9294 11.2696C15.0605 11.0818 15.1307 10.8582 15.1307 10.6292C15.1307 10.4002 15.0605 10.1767 14.9294 9.98884C14.7984 9.801 14.6129 9.65786 14.398 9.57875L14.3952 9.57453ZM10.1357 12.0467C10.0593 12.0749 9.98998 12.1192 9.93244 12.1768C9.8749 12.2343 9.83053 12.3036 9.80239 12.38L8.37786 16.2395L6.95614 12.3828C6.92804 12.3057 6.8834 12.2356 6.82534 12.1775C6.76727 12.1195 6.6972 12.0748 6.62005 12.0467L2.76341 10.625L6.62005 9.20328C6.6972 9.17519 6.76727 9.13054 6.82534 9.07248C6.8834 9.01441 6.92804 8.94434 6.95614 8.86719L8.37786 5.01055L9.79958 8.86719C9.82772 8.94354 9.87209 9.01288 9.92963 9.07042C9.98717 9.12796 10.0565 9.17233 10.1329 9.20047L13.9923 10.625L10.1357 12.0467ZM10.6279 3.3125C10.6279 3.16332 10.6871 3.02024 10.7926 2.91475C10.8981 2.80926 11.0412 2.75 11.1904 2.75H12.3154V1.625C12.3154 1.47582 12.3746 1.33274 12.4801 1.22725C12.5856 1.12176 12.7287 1.0625 12.8779 1.0625C13.027 1.0625 13.1701 1.12176 13.2756 1.22725C13.3811 1.33274 13.4404 1.47582 13.4404 1.625V2.75H14.5654C14.7145 2.75 14.8576 2.80926 14.9631 2.91475C15.0686 3.02024 15.1279 3.16332 15.1279 3.3125C15.1279 3.46168 15.0686 3.60476 14.9631 3.71025C14.8576 3.81574 14.7145 3.875 14.5654 3.875H13.4404V5C13.4404 5.14918 13.3811 5.29226 13.2756 5.39775C13.1701 5.50324 13.027 5.5625 12.8779 5.5625C12.7287 5.5625 12.5856 5.50324 12.4801 5.39775C12.3746 5.29226 12.3154 5.14918 12.3154 5V3.875H11.1904C11.0412 3.875 10.8981 3.81574 10.7926 3.71025C10.6871 3.60476 10.6279 3.46168 10.6279 3.3125ZM17.9404 6.6875C17.9404 6.83668 17.8811 6.97976 17.7756 7.08525C17.6701 7.19074 17.527 7.25 17.3779 7.25H16.8154V7.8125C16.8154 7.96168 16.7561 8.10476 16.6506 8.21025C16.5451 8.31574 16.402 8.375 16.2529 8.375C16.1037 8.375 15.9606 8.31574 15.8551 8.21025C15.7496 8.10476 15.6904 7.96168 15.6904 7.8125V7.25H15.1279C14.9787 7.25 14.8356 7.19074 14.7301 7.08525C14.6246 6.97976 14.5654 6.83668 14.5654 6.6875C14.5654 6.53832 14.6246 6.39524 14.7301 6.28975C14.8356 6.18426 14.9787 6.125 15.1279 6.125H15.6904V5.5625C15.6904 5.41332 15.7496 5.27024 15.8551 5.16475C15.9606 5.05926 16.1037 5 16.2529 5C16.402 5 16.5451 5.05926 16.6506 5.16475C16.7561 5.27024 16.8154 5.41332 16.8154 5.5625V6.125H17.3779C17.527 6.125 17.6701 6.18426 17.7756 6.28975C17.8811 6.39524 17.9404 6.53832 17.9404 6.6875Z"
        fill="currentColor"
      />
    </svg>
  );
}

function OfferPracticeIcon() {
  return (
    <svg width="100%" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M29.5002 26.9953H23.4202C26.0693 25.3263 28.0267 22.7568 28.9322 19.7595C29.8378 16.7623 29.6306 13.5388 28.3488 10.6821C27.067 7.82545 24.7967 5.5277 21.9557 4.21155C19.1147 2.89539 15.894 2.64937 12.886 3.51872C9.87805 4.38807 7.28516 6.31434 5.58431 8.94317C3.88347 11.572 3.18906 14.7266 3.62884 17.8266C4.06862 20.9267 5.613 23.7636 7.97788 25.8157C10.3428 27.8678 13.3691 28.9969 16.5002 28.9953H29.5002C29.7654 28.9953 30.0198 28.89 30.2073 28.7024C30.3948 28.5149 30.5002 28.2605 30.5002 27.9953C30.5002 27.7301 30.3948 27.4757 30.2073 27.2882C30.0198 27.1007 29.7654 26.9953 29.5002 26.9953ZM10.5002 18.4953C10.0057 18.4953 9.52238 18.3487 9.11126 18.074C8.70013 17.7993 8.3797 17.4088 8.19048 16.952C8.00126 16.4952 7.95176 15.9925 8.04822 15.5076C8.14468 15.0226 8.38278 14.5772 8.73242 14.2276C9.08205 13.8779 9.5275 13.6398 10.0125 13.5434C10.4974 13.4469 11.0001 13.4964 11.4569 13.6856C11.9137 13.8748 12.3042 14.1953 12.5789 14.6064C12.8536 15.0175 13.0002 15.5009 13.0002 15.9953C13.0002 16.6584 12.7368 17.2942 12.2679 17.7631C11.7991 18.2319 11.1632 18.4953 10.5002 18.4953ZM16.5002 24.4953C16.0057 24.4953 15.5224 24.3487 15.1113 24.074C14.7001 23.7993 14.3797 23.4088 14.1905 22.952C14.0013 22.4952 13.9518 21.9925 14.0482 21.5076C14.1447 21.0226 14.3828 20.5772 14.7324 20.2276C15.082 19.8779 15.5275 19.6398 16.0125 19.5434C16.4974 19.4469 17.0001 19.4964 17.4569 19.6856C17.9137 19.8748 18.3042 20.1953 18.5789 20.6064C18.8536 21.0175 19.0002 21.5009 19.0002 21.9953C19.0002 22.6584 18.7368 23.2942 18.2679 23.7631C17.7991 24.2319 17.1632 24.4953 16.5002 24.4953ZM16.5002 12.4953C16.0057 12.4953 15.5224 12.3487 15.1113 12.074C14.7001 11.7993 14.3797 11.4088 14.1905 10.952C14.0013 10.4952 13.9518 9.99255 14.0482 9.50759C14.1447 9.02264 14.3828 8.57718 14.7324 8.22755C15.082 7.87792 15.5275 7.63982 16.0125 7.54336C16.4974 7.44689 17.0001 7.4964 17.4569 7.68562C17.9137 7.87484 18.3042 8.19527 18.5789 8.60639C18.8536 9.01752 19.0002 9.50087 19.0002 9.99532C19.0002 10.6584 18.7368 11.2942 18.2679 11.7631C17.7991 12.2319 17.1632 12.4953 16.5002 12.4953ZM20.0002 15.9953C20.0002 15.5009 20.1468 15.0175 20.4215 14.6064C20.6962 14.1953 21.0867 13.8748 21.5435 13.6856C22.0003 13.4964 22.503 13.4469 22.9879 13.5434C23.4729 13.6398 23.9183 13.8779 24.268 14.2276C24.6176 14.5772 24.8557 15.0226 24.9521 15.5076C25.0486 15.9925 24.9991 16.4952 24.8099 16.952C24.6207 17.4088 24.3002 17.7993 23.8891 18.074C23.478 18.3487 22.9946 18.4953 22.5002 18.4953C21.8371 18.4953 21.2013 18.2319 20.7324 17.7631C20.2636 17.2942 20.0002 16.6584 20.0002 15.9953Z" fill="currentColor" />
    </svg>
  );
}

function OfferPerformanceIcon() {
  return (
    <svg width="100%" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M26.5 4H23.5V3C23.5 2.73478 23.3946 2.48043 23.2071 2.29289C23.0196 2.10536 22.7652 2 22.5 2C22.2348 2 21.9804 2.10536 21.7929 2.29289C21.6054 2.48043 21.5 2.73478 21.5 3V4H11.5V3C11.5 2.73478 11.3946 2.48043 11.2071 2.29289C11.0196 2.10536 10.7652 2 10.5 2C10.2348 2 9.98043 2.10536 9.79289 2.29289C9.60536 2.48043 9.5 2.73478 9.5 3V4H6.5C5.96957 4 5.46086 4.21071 5.08579 4.58579C4.71071 4.96086 4.5 5.46957 4.5 6V26C4.5 26.5304 4.71071 27.0391 5.08579 27.4142C5.46086 27.7893 5.96957 28 6.5 28H26.5C27.0304 28 27.5391 27.7893 27.9142 27.4142C28.2893 27.0391 28.5 26.5304 28.5 26V6C28.5 5.46957 28.2893 4.96086 27.9142 4.58579C27.5391 4.21071 27.0304 4 26.5 4ZM9.5 8V7C9.5 6.73478 9.60536 6.48043 9.79289 6.29289C9.98043 6.10536 10.2348 6 10.5 6C10.7652 6 11.0196 6.10536 11.2071 6.29289C11.3946 6.48043 11.5 6.73478 11.5 7V8C11.5 8.26522 11.3946 8.51957 11.2071 8.70711C11.0196 8.89464 10.7652 9 10.5 9C10.2348 9 9.98043 8.89464 9.79289 8.70711C9.60536 8.51957 9.5 8.26522 9.5 8ZM19.82 20.8838C18.8622 21.6771 17.8246 22.3688 16.7237 22.9475C16.6543 22.9823 16.5777 23.0004 16.5 23.0004C16.4223 23.0004 16.3457 22.9823 16.2762 22.9475C15.1754 22.3688 14.1378 21.6771 13.18 20.8838C11.07 19.125 10 17.3138 10 15.5C10.0003 14.7349 10.2514 13.991 10.7147 13.3821C11.178 12.7732 11.828 12.333 12.5653 12.1286C13.3026 11.9243 14.0866 11.9672 14.7972 12.2508C15.5078 12.5344 16.1059 13.043 16.5 13.6987C16.8941 13.043 17.4922 12.5344 18.2028 12.2508C18.9134 11.9672 19.6974 11.9243 20.4347 12.1286C21.172 12.333 21.822 12.7732 22.2853 13.3821C22.7486 13.991 22.9997 14.7349 23 15.5C23 17.3138 21.93 19.125 19.82 20.8838ZM23.5 8C23.5 8.26522 23.3946 8.51957 23.2071 8.70711C23.0196 8.89464 22.7652 9 22.5 9C22.2348 9 21.9804 8.89464 21.7929 8.70711C21.6054 8.51957 21.5 8.26522 21.5 8V7C21.5 6.73478 21.6054 6.48043 21.7929 6.29289C21.9804 6.10536 22.2348 6 22.5 6C22.7652 6 23.0196 6.10536 23.2071 6.29289C23.3946 6.48043 23.5 6.73478 23.5 7V8Z" fill="currentColor" />
    </svg>
  );
}

function PricingBundleIcon() {
  return (
    <svg width="100%" viewBox="0 0 41 37" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M28.1373 16.0519H24.5222C26.0973 15.0595 27.2612 13.5317 27.7996 11.7495C28.338 9.96738 28.2148 8.05073 27.4527 6.35217C26.6905 4.65361 25.3407 3.28737 23.6514 2.5048C21.9622 1.72222 20.0471 1.57593 18.2586 2.09284C16.4701 2.60976 14.9284 3.7551 13.9171 5.31819C12.9057 6.88128 12.4929 8.75698 12.7543 10.6002C13.0158 12.4435 13.9341 14.1304 15.3403 15.3505C16.7464 16.5706 18.5459 17.242 20.4076 17.2411H28.1373C28.295 17.2411 28.4462 17.1784 28.5577 17.0669C28.6693 16.9554 28.7319 16.8042 28.7319 16.6465C28.7319 16.4888 28.6693 16.3376 28.5577 16.2261C28.4462 16.1145 28.295 16.0519 28.1373 16.0519ZM16.84 10.9979C16.546 10.9979 16.2586 10.9107 16.0142 10.7473C15.7697 10.584 15.5792 10.3518 15.4667 10.0802C15.3542 9.8086 15.3247 9.50971 15.3821 9.22136C15.4394 8.93301 15.581 8.66815 15.7889 8.46026C15.9968 8.25237 16.2617 8.1108 16.55 8.05344C16.8384 7.99608 17.1372 8.02552 17.4089 8.13803C17.6805 8.25054 17.9126 8.44106 18.076 8.68552C18.2393 8.92997 18.3265 9.21736 18.3265 9.51136C18.3265 9.9056 18.1699 10.2837 17.8911 10.5625C17.6123 10.8412 17.2342 10.9979 16.84 10.9979ZM20.4076 14.5654C20.1136 14.5654 19.8262 14.4782 19.5817 14.3149C19.3373 14.1516 19.1467 13.9194 19.0342 13.6478C18.9217 13.3762 18.8923 13.0773 18.9496 12.7889C19.007 12.5006 19.1486 12.2357 19.3565 12.0278C19.5644 11.8199 19.8292 11.6784 20.1176 11.621C20.4059 11.5637 20.7048 11.5931 20.9764 11.7056C21.248 11.8181 21.4802 12.0086 21.6435 12.2531C21.8069 12.4975 21.8941 12.7849 21.8941 13.0789C21.8941 13.4732 21.7374 13.8513 21.4587 14.13C21.1799 14.4088 20.8018 14.5654 20.4076 14.5654ZM20.4076 7.43028C20.1136 7.43028 19.8262 7.3431 19.5817 7.17976C19.3373 7.01643 19.1467 6.78427 19.0342 6.51265C18.9217 6.24103 18.8923 5.94215 18.9496 5.6538C19.007 5.36545 19.1486 5.10058 19.3565 4.89269C19.5644 4.6848 19.8292 4.54323 20.1176 4.48587C20.4059 4.42852 20.7048 4.45795 20.9764 4.57046C21.248 4.68297 21.4802 4.8735 21.6435 5.11795C21.8069 5.3624 21.8941 5.6498 21.8941 5.9438C21.8941 6.33804 21.7374 6.71613 21.4587 6.9949C21.1799 7.27367 20.8018 7.43028 20.4076 7.43028ZM22.4887 9.51136C22.4887 9.21736 22.5758 8.92997 22.7392 8.68552C22.9025 8.44106 23.1347 8.25054 23.4063 8.13803C23.6779 8.02552 23.9768 7.99608 24.2651 8.05344C24.5535 8.1108 24.8184 8.25237 25.0262 8.46026C25.2341 8.66815 25.3757 8.93301 25.4331 9.22136C25.4904 9.50971 25.461 9.8086 25.3485 10.0802C25.236 10.3518 25.0454 10.584 24.801 10.7473C24.5565 10.9107 24.2691 10.9979 23.9751 10.9979C23.5809 10.9979 23.2028 10.8412 22.924 10.5625C22.6453 10.2837 22.4887 9.9056 22.4887 9.51136Z" fill="currentColor" />
      <path d="M17.7408 34.029H14.1257C15.7008 33.0366 16.8647 31.5087 17.4031 29.7266C17.9416 27.9444 17.8184 26.0278 17.0562 24.3292C16.294 22.6307 14.9442 21.2644 13.2549 20.4818C11.5657 19.6993 9.65065 19.553 7.86213 20.0699C6.07361 20.5868 4.53189 21.7322 3.52057 23.2952C2.50926 24.8583 2.09637 26.734 2.35786 28.5773C2.61935 30.4206 3.53763 32.1074 4.94378 33.3276C6.34993 34.5477 8.14937 35.2191 10.0111 35.2181H17.7408C17.8985 35.2181 18.0498 35.1555 18.1613 35.044C18.2728 34.9325 18.3354 34.7812 18.3354 34.6235C18.3354 34.4659 18.2728 34.3146 18.1613 34.2031C18.0498 34.0916 17.8985 34.029 17.7408 34.029ZM6.44352 28.9749C6.14952 28.9749 5.86212 28.8877 5.61767 28.7244C5.37322 28.561 5.18269 28.3289 5.07019 28.0573C4.95768 27.7856 4.92824 27.4868 4.9856 27.1984C5.04295 26.9101 5.18453 26.6452 5.39242 26.4373C5.6003 26.2294 5.86517 26.0878 6.15352 26.0305C6.44187 25.9731 6.74075 26.0026 7.01237 26.1151C7.28399 26.2276 7.51615 26.4181 7.67949 26.6626C7.84283 26.907 7.93001 27.1944 7.93001 27.4884C7.93001 27.8827 7.7734 28.2607 7.49463 28.5395C7.21585 28.8183 6.83776 28.9749 6.44352 28.9749ZM10.0111 32.5425C9.71709 32.5425 9.42969 32.4553 9.18524 32.292C8.94079 32.1286 8.75026 31.8965 8.63775 31.6248C8.52524 31.3532 8.49581 31.0543 8.55316 30.766C8.61052 30.4776 8.75209 30.2128 8.95998 30.0049C9.16787 29.797 9.43274 29.6554 9.72109 29.5981C10.0094 29.5407 10.3083 29.5701 10.5799 29.6826C10.8516 29.7952 11.0837 29.9857 11.2471 30.2301C11.4104 30.4746 11.4976 30.762 11.4976 31.056C11.4976 31.4502 11.341 31.8283 11.0622 32.1071C10.7834 32.3859 10.4053 32.5425 10.0111 32.5425ZM10.0111 25.4073C9.71709 25.4073 9.42969 25.3202 9.18524 25.1568C8.94079 24.9935 8.75026 24.7613 8.63775 24.4897C8.52524 24.2181 8.49581 23.9192 8.55316 23.6308C8.61052 23.3425 8.75209 23.0776 8.95998 22.8697C9.16787 22.6619 9.43274 22.5203 9.72109 22.4629C10.0094 22.4056 10.3083 22.435 10.5799 22.5475C10.8516 22.66 11.0837 22.8505 11.2471 23.095C11.4104 23.3395 11.4976 23.6268 11.4976 23.9208C11.4976 24.3151 11.341 24.6932 11.0622 24.972C10.7834 25.2507 10.4053 25.4073 10.0111 25.4073ZM12.0922 27.4884C12.0922 27.1944 12.1794 26.907 12.3427 26.6626C12.506 26.4181 12.7382 26.2276 13.0098 26.1151C13.2814 26.0026 13.5803 25.9731 13.8687 26.0305C14.157 26.0878 14.4219 26.2294 14.6298 26.4373C14.8376 26.6452 14.9792 26.9101 15.0366 27.1984C15.0939 27.4868 15.0645 27.7856 14.952 28.0573C14.8395 28.3289 14.649 28.561 14.4045 28.7244C14.1601 28.8877 13.8727 28.9749 13.5787 28.9749C13.1844 28.9749 12.8063 28.8183 12.5276 28.5395C12.2488 28.2607 12.0922 27.8827 12.0922 27.4884Z" fill="currentColor" />
      <path d="M38.5318 34.029H34.9167C36.4919 33.0366 37.6557 31.5087 38.1941 29.7266C38.7326 27.9444 38.6094 26.0278 37.8472 24.3292C37.0851 22.6307 35.7352 21.2644 34.0459 20.4818C32.3567 19.6993 30.4417 19.553 28.6531 20.0699C26.8646 20.5868 25.3229 21.7322 24.3116 23.2952C23.3003 24.8583 22.8874 26.734 23.1489 28.5773C23.4104 30.4206 24.3286 32.1074 25.7348 33.3276C27.1409 34.5477 28.9404 35.2191 30.8021 35.2181H38.5318C38.6895 35.2181 38.8408 35.1555 38.9523 35.044C39.0638 34.9325 39.1264 34.7812 39.1264 34.6235C39.1264 34.4659 39.0638 34.3146 38.9523 34.2031C38.8408 34.0916 38.6895 34.029 38.5318 34.029ZM27.2345 28.9749C26.9405 28.9749 26.6531 28.8877 26.4087 28.7244C26.1642 28.561 25.9737 28.3289 25.8612 28.0573C25.7487 27.7856 25.7193 27.4868 25.7766 27.1984C25.834 26.9101 25.9755 26.6452 26.1834 26.4373C26.3913 26.2294 26.6562 26.0878 26.9445 26.0305C27.2329 25.9731 27.5318 26.0026 27.8034 26.1151C28.075 26.2276 28.3072 26.4181 28.4705 26.6626C28.6338 26.907 28.721 27.1944 28.721 27.4884C28.721 27.8827 28.5644 28.2607 28.2856 28.5395C28.0069 28.8183 27.6288 28.9749 27.2345 28.9749ZM30.8021 32.5425C30.5081 32.5425 30.2207 32.4553 29.9763 32.292C29.7318 32.1286 29.5413 31.8965 29.4288 31.6248C29.3163 31.3532 29.2868 31.0543 29.3442 30.766C29.4015 30.4776 29.5431 30.2128 29.751 30.0049C29.9589 29.797 30.2238 29.6554 30.5121 29.5981C30.8005 29.5407 31.0993 29.5701 31.371 29.6826C31.6426 29.7952 31.8747 29.9857 32.0381 30.2301C32.2014 30.4746 32.2886 30.762 32.2886 31.056C32.2886 31.4502 32.132 31.8283 31.8532 32.1071C31.5744 32.3859 31.1963 32.5425 30.8021 32.5425ZM30.8021 25.4073C30.5081 25.4073 30.2207 25.3202 29.9763 25.1568C29.7318 24.9935 29.5413 24.7613 29.4288 24.4897C29.3163 24.2181 29.2868 23.9192 29.3442 23.6308C29.4015 23.3425 29.5431 23.0776 29.751 22.8697C29.9589 22.6619 30.2238 22.5203 30.5121 22.4629C30.8005 22.4056 31.0993 22.435 31.371 22.5475C31.6426 22.66 31.8747 22.8505 32.0381 23.095C32.2014 23.3395 32.2886 23.6268 32.2886 23.9208C32.2886 24.3151 32.132 24.6932 31.8532 24.972C31.5744 25.2507 31.1963 25.4073 30.8021 25.4073ZM32.8832 27.4884C32.8832 27.1944 32.9704 26.907 33.1337 26.6626C33.297 26.4181 33.5292 26.2276 33.8008 26.1151C34.0724 26.0026 34.3713 25.9731 34.6597 26.0305C34.948 26.0878 35.2129 26.2294 35.4208 26.4373C35.6287 26.6452 35.7702 26.9101 35.8276 27.1984C35.885 27.4868 35.8555 27.7856 35.743 28.0573C35.6305 28.3289 35.44 28.561 35.1955 28.7244C34.9511 28.8877 34.6637 28.9749 34.3697 28.9749C33.9754 28.9749 33.5973 28.8183 33.3186 28.5395C33.0398 28.2607 32.8832 27.8827 32.8832 27.4884Z" fill="currentColor" />
    </svg>
  );
}

function PricingPracticeAddOnIcon({ className }: { className?: string }) {
  return (
    <svg width="100%" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
      <path opacity="0.2" d="M3.125 3.75C2.95924 3.75 2.80027 3.81585 2.68306 3.93306C2.56585 4.05027 2.5 4.20924 2.5 4.375V15.625C2.5 15.7908 2.56585 15.9497 2.68306 16.0669C2.80027 16.1842 2.95924 16.25 3.125 16.25H12.5V3.75H3.125ZM7.5 11.25C7.12916 11.25 6.76665 11.14 6.45831 10.934C6.14996 10.728 5.90964 10.4351 5.76773 10.0925C5.62581 9.74992 5.58868 9.37292 5.66103 9.00921C5.73337 8.64549 5.91195 8.3114 6.17417 8.04917C6.4364 7.78695 6.77049 7.60837 7.13421 7.53603C7.49792 7.46368 7.87492 7.50081 8.21753 7.64273C8.56014 7.78464 8.85298 8.02496 9.05901 8.33331C9.26503 8.64165 9.375 9.00416 9.375 9.375C9.375 9.87228 9.17746 10.3492 8.82583 10.7008C8.47419 11.0525 7.99728 11.25 7.5 11.25Z" fill="#762A5C" />
      <path d="M16.875 3.125H3.125C2.79348 3.125 2.47554 3.2567 2.24112 3.49112C2.0067 3.72554 1.875 4.04348 1.875 4.375V15.625C1.875 15.9565 2.0067 16.2745 2.24112 16.5089C2.47554 16.7433 2.79348 16.875 3.125 16.875H16.875C17.2065 16.875 17.5245 16.7433 17.7589 16.5089C17.9933 16.2745 18.125 15.9565 18.125 15.625V4.375C18.125 4.04348 17.9933 3.72554 17.7589 3.49112C17.5245 3.2567 17.2065 3.125 16.875 3.125ZM16.875 9.375H13.125V4.375H16.875V9.375ZM3.125 4.375H11.875V15.625H3.125V4.375ZM16.875 15.625H13.125V10.625H16.875V15.625ZM14.0625 6.875C14.0625 6.68958 14.1175 6.50832 14.2205 6.35415C14.3235 6.19998 14.4699 6.07982 14.6412 6.00886C14.8125 5.93791 15.001 5.91934 15.1829 5.95551C15.3648 5.99169 15.5318 6.08098 15.6629 6.21209C15.794 6.3432 15.8833 6.51025 15.9195 6.6921C15.9557 6.87396 15.9371 7.06246 15.8661 7.23377C15.7952 7.40507 15.675 7.55149 15.5208 7.6545C15.3667 7.75752 15.1854 7.8125 15 7.8125C14.7514 7.8125 14.5129 7.71373 14.3371 7.53791C14.1613 7.3621 14.0625 7.12364 14.0625 6.875ZM15.9375 13.125C15.9375 13.3104 15.8825 13.4917 15.7795 13.6458C15.6765 13.8 15.5301 13.9202 15.3588 13.9911C15.1875 14.0621 14.999 14.0807 14.8171 14.0445C14.6352 14.0083 14.4682 13.919 14.3371 13.7879C14.206 13.6568 14.1167 13.4898 14.0805 13.3079C14.0443 13.126 14.0629 12.9375 14.1339 12.7662C14.2048 12.5949 14.325 12.4485 14.4792 12.3455C14.6333 12.2425 14.8146 12.1875 15 12.1875C15.2486 12.1875 15.4871 12.2863 15.6629 12.4621C15.8387 12.6379 15.9375 12.8764 15.9375 13.125ZM10.6055 12.9688C10.4032 12.2136 9.92366 11.5625 9.2625 11.1453C9.61362 10.7963 9.8532 10.3508 9.95087 9.86549C10.0485 9.38013 9.9999 8.8767 9.81112 8.41902C9.62234 7.96134 9.30192 7.57001 8.89047 7.29465C8.47903 7.01929 7.99509 6.87229 7.5 6.87229C7.00491 6.87229 6.52097 7.01929 6.10953 7.29465C5.69808 7.57001 5.37766 7.96134 5.18888 8.41902C5.0001 8.8767 4.95146 9.38013 5.04913 9.86549C5.1468 10.3508 5.38638 10.7963 5.7375 11.1453C5.07676 11.5629 4.59734 12.2139 4.39453 12.9688C4.35309 13.1293 4.37714 13.2998 4.46138 13.4426C4.54563 13.5855 4.68317 13.689 4.84375 13.7305C5.00433 13.7719 5.1748 13.7479 5.31765 13.6636C5.4605 13.5794 5.56403 13.4418 5.60547 13.2812C5.81172 12.4805 6.62578 11.875 7.5 11.875C8.37422 11.875 9.18906 12.4789 9.39453 13.2812C9.43597 13.4418 9.5395 13.5794 9.68235 13.6636C9.8252 13.7479 9.99567 13.7719 10.1562 13.7305C10.3168 13.689 10.4544 13.5855 10.5386 13.4426C10.6229 13.2998 10.6469 13.1293 10.6055 12.9688ZM6.25 9.375C6.25 9.12777 6.32331 8.8861 6.46066 8.68054C6.59801 8.47498 6.79324 8.31476 7.02165 8.22015C7.25005 8.12554 7.50139 8.10079 7.74386 8.14902C7.98634 8.19725 8.20907 8.3163 8.38388 8.49112C8.5587 8.66593 8.67775 8.88866 8.72598 9.13114C8.77421 9.37361 8.74946 9.62495 8.65485 9.85335C8.56024 10.0818 8.40002 10.277 8.19446 10.4143C7.9889 10.5517 7.74723 10.625 7.5 10.625C7.16848 10.625 6.85054 10.4933 6.61612 10.2589C6.3817 10.0245 6.25 9.70652 6.25 9.375Z" fill="#762A5C" />
    </svg>
  );
}

function PricingCalendarIcon({ className }: { className?: string }) {
  return (
    <svg width="100%" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
      <path opacity="0.2" d="M15.1875 3.375V6.1875H2.8125V3.375C2.8125 3.22582 2.87176 3.08274 2.97725 2.97725C3.08274 2.87176 3.22582 2.8125 3.375 2.8125H14.625C14.7742 2.8125 14.9173 2.87176 15.0227 2.97725C15.1282 3.08274 15.1875 3.22582 15.1875 3.375Z" fill="#762A5C" />
      <path d="M14.625 2.25H12.9375V1.6875C12.9375 1.53832 12.8782 1.39524 12.7727 1.28975C12.6673 1.18426 12.5242 1.125 12.375 1.125C12.2258 1.125 12.0827 1.18426 11.9773 1.28975C11.8718 1.39524 11.8125 1.53832 11.8125 1.6875V2.25H6.1875V1.6875C6.1875 1.53832 6.12824 1.39524 6.02275 1.28975C5.91726 1.18426 5.77418 1.125 5.625 1.125C5.47582 1.125 5.33274 1.18426 5.22725 1.28975C5.12176 1.39524 5.0625 1.53832 5.0625 1.6875V2.25H3.375C3.07663 2.25 2.79048 2.36853 2.5795 2.5795C2.36853 2.79048 2.25 3.07663 2.25 3.375V14.625C2.25 14.9234 2.36853 15.2095 2.5795 15.4205C2.79048 15.6315 3.07663 15.75 3.375 15.75H14.625C14.9234 15.75 15.2095 15.6315 15.4205 15.4205C15.6315 15.2095 15.75 14.9234 15.75 14.625V3.375C15.75 3.07663 15.6315 2.79048 15.4205 2.5795C15.2095 2.36853 14.9234 2.25 14.625 2.25ZM5.0625 3.375V3.9375C5.0625 4.08668 5.12176 4.22976 5.22725 4.33525C5.33274 4.44074 5.47582 4.5 5.625 4.5C5.77418 4.5 5.91726 4.44074 6.02275 4.33525C6.12824 4.22976 6.1875 4.08668 6.1875 3.9375V3.375H11.8125V3.9375C11.8125 4.08668 11.8718 4.22976 11.9773 4.33525C12.0827 4.44074 12.2258 4.5 12.375 4.5C12.5242 4.5 12.6673 4.44074 12.7727 4.33525C12.8782 4.22976 12.9375 4.08668 12.9375 3.9375V3.375H14.625V5.625H3.375V3.375H5.0625ZM14.625 14.625H3.375V6.75H14.625V14.625ZM9.84375 9.28125C9.84375 9.44813 9.79426 9.61126 9.70155 9.75001C9.60884 9.88877 9.47706 9.99691 9.32289 10.0608C9.16871 10.1246 8.99906 10.1413 8.83539 10.1088C8.67172 10.0762 8.52138 9.99587 8.40338 9.87787C8.28538 9.75987 8.20502 9.60953 8.17246 9.44586C8.13991 9.28219 8.15662 9.11254 8.22048 8.95836C8.28434 8.80419 8.39248 8.67241 8.53124 8.5797C8.66999 8.48698 8.83312 8.4375 9 8.4375C9.22378 8.4375 9.43839 8.52639 9.59662 8.68463C9.75485 8.84286 9.84375 9.05747 9.84375 9.28125ZM12.9375 9.28125C12.9375 9.44813 12.888 9.61126 12.7953 9.75001C12.7026 9.88877 12.5708 9.99691 12.4166 10.0608C12.2625 10.1246 12.0928 10.1413 11.9291 10.1088C11.7655 10.0762 11.6151 9.99587 11.4971 9.87787C11.3791 9.75987 11.2988 9.60953 11.2662 9.44586C11.2337 9.28219 11.2504 9.11254 11.3142 8.95836C11.3781 8.80419 11.4862 8.67241 11.625 8.5797C11.7637 8.48698 11.9269 8.4375 12.0938 8.4375C12.3175 8.4375 12.5321 8.52639 12.6904 8.68463C12.8486 8.84286 12.9375 9.05747 12.9375 9.28125ZM6.75 12.0938C6.75 12.2606 6.70052 12.4238 6.6078 12.5625C6.51509 12.7013 6.38331 12.8094 6.22914 12.8733C6.07496 12.9371 5.90531 12.9538 5.74164 12.9213C5.57797 12.8887 5.42763 12.8084 5.30963 12.6904C5.19163 12.5724 5.11127 12.422 5.07871 12.2584C5.04616 12.0947 5.06287 11.925 5.12673 11.7709C5.19059 11.6167 5.29873 11.4849 5.43749 11.3922C5.57624 11.2995 5.73937 11.25 5.90625 11.25C6.13003 11.25 6.34464 11.3389 6.50287 11.4971C6.66111 11.6554 6.75 11.87 6.75 12.0938ZM9.84375 12.0938C9.84375 12.2606 9.79426 12.4238 9.70155 12.5625C9.60884 12.7013 9.47706 12.8094 9.32289 12.8733C9.16871 12.9371 8.99906 12.9538 8.83539 12.9213C8.67172 12.8887 8.52138 12.8084 8.40338 12.6904C8.28538 12.5724 8.20502 12.422 8.17246 12.2584C8.13991 12.0947 8.15662 11.925 8.22048 11.7709C8.28434 11.6167 8.39248 11.4849 8.53124 11.3922C8.66999 11.2995 8.83312 11.25 9 11.25C9.22378 11.25 9.43839 11.3389 9.59662 11.4971C9.75485 11.6554 9.84375 11.87 9.84375 12.0938ZM12.9375 12.0938C12.9375 12.2606 12.888 12.4238 12.7953 12.5625C12.7026 12.7013 12.5708 12.8094 12.4166 12.8733C12.2625 12.9371 12.0928 12.9538 11.9291 12.9213C11.7655 12.8887 11.6151 12.8084 11.4971 12.6904C11.3791 12.5724 11.2988 12.422 11.2662 12.2584C11.2337 12.0947 11.2504 11.925 11.3142 11.7709C11.3781 11.6167 11.4862 11.4849 11.625 11.3922C11.7637 11.2995 11.9269 11.25 12.0938 11.25C12.3175 11.25 12.5321 11.3389 12.6904 11.4971C12.8486 11.6554 12.9375 11.87 12.9375 12.0938Z" fill="#762A5C" />
    </svg>
  );
}

function PricingMusicMixIcon({ className }: { className?: string }) {
  return <Music className={className} strokeWidth={2.1} aria-hidden="true" />;
}

function PricingBestValueIcon({ className }: { className?: string }) {
  return (
    <svg width="100%" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
      <path d="M15.2028 5.77357L13.449 7.25357L13.984 9.46544C14.0228 9.62557 14.0133 9.79361 13.9567 9.94834C13.9001 10.1031 13.7989 10.2376 13.6659 10.3348C13.5313 10.4337 13.3705 10.4906 13.2037 10.4982C13.0369 10.5059 12.8715 10.4641 12.7284 10.3779L10.7503 9.18732L8.77215 10.3786C8.62915 10.4649 8.46374 10.5069 8.29686 10.4992C8.12998 10.4916 7.96913 10.4346 7.83465 10.3354C7.70142 10.2384 7.6 10.1039 7.54326 9.94916C7.48651 9.79439 7.47699 9.62625 7.5159 9.46607L8.0509 7.25419L6.29778 5.77357C6.17082 5.66625 6.0787 5.52356 6.03316 5.36368C5.98762 5.2038 5.99073 5.03398 6.04209 4.87587C6.09345 4.71776 6.19074 4.57854 6.32154 4.47594C6.45234 4.37335 6.61074 4.31203 6.77653 4.29982L9.08528 4.11732L9.9734 2.01357C10.0383 1.86122 10.1465 1.73131 10.2846 1.64C10.4228 1.54868 10.5847 1.5 10.7503 1.5C10.9159 1.5 11.0778 1.54868 11.2159 1.64C11.354 1.73131 11.4623 1.86122 11.5272 2.01357L12.4153 4.11732L14.724 4.29982C14.8898 4.31203 15.0482 4.37335 15.179 4.47594C15.3098 4.57854 15.4071 4.71776 15.4585 4.87587C15.5098 5.03398 15.5129 5.2038 15.4674 5.36368C15.4219 5.52356 15.3297 5.66625 15.2028 5.77357ZM6.00715 7.49294C5.96072 7.44645 5.90557 7.40957 5.84487 7.38441C5.78417 7.35925 5.71911 7.3463 5.6534 7.3463C5.5877 7.3463 5.52263 7.35925 5.46193 7.38441C5.40123 7.40957 5.34609 7.44645 5.29965 7.49294L1.64653 11.1461C1.55271 11.2399 1.5 11.3671 1.5 11.4998C1.5 11.6325 1.55271 11.7597 1.64653 11.8536C1.74035 11.9474 1.8676 12.0001 2.00028 12.0001C2.13296 12.0001 2.26021 11.9474 2.35403 11.8536L6.00715 8.20044C6.05364 8.15401 6.09052 8.09886 6.11568 8.03816C6.14085 7.97746 6.1538 7.9124 6.1538 7.84669C6.1538 7.78098 6.14085 7.71592 6.11568 7.65522C6.09052 7.59452 6.05364 7.53938 6.00715 7.49294ZM5.9759 11.3173L2.64653 14.6461C2.60007 14.6925 2.56322 14.7477 2.53808 14.8084C2.51294 14.8691 2.5 14.9341 2.5 14.9998C2.5 15.0655 2.51294 15.1306 2.53808 15.1913C2.56322 15.252 2.60007 15.3071 2.64653 15.3536C2.74035 15.4474 2.8676 15.5001 3.00028 15.5001C3.06598 15.5001 3.13103 15.4872 3.19173 15.462C3.25242 15.4369 3.30757 15.4 3.35403 15.3536L6.68278 12.0242C6.77259 11.9297 6.82192 11.8038 6.82025 11.6734C6.81858 11.543 6.76604 11.4185 6.67384 11.3263C6.58164 11.2341 6.45707 11.1815 6.32669 11.1798C6.19631 11.1782 6.07043 11.2275 5.9759 11.3173ZM10.5384 11.2548L7.14528 14.6473C7.09882 14.6938 7.06197 14.7489 7.03683 14.8096C7.01169 14.8703 6.99875 14.9354 6.99875 15.0011C6.99875 15.0668 7.01169 15.1318 7.03683 15.1925C7.06197 15.2532 7.09882 15.3084 7.14528 15.3548C7.2391 15.4486 7.36635 15.5013 7.49903 15.5013C7.56473 15.5013 7.62978 15.4884 7.69048 15.4633C7.75117 15.4381 7.80632 15.4013 7.85278 15.3548L11.2453 11.9623C11.3391 11.8686 11.3918 11.7414 11.3919 11.6088C11.392 11.4762 11.3393 11.3489 11.2456 11.2551C11.1519 11.1613 11.0247 11.1086 10.8921 11.1085C10.7594 11.1085 10.6322 11.1611 10.5384 11.2548Z" fill="currentColor" />
    </svg>
  );
}

function PricingPlanIcon({ planKey }: { planKey: PricingPlan["key"] }) {
  if (planKey === "signature") {
    return <PricingBundleIcon />;
  }

  if (planKey === "build") {
    return <ProcessLibraryIcon />;
  }

  return <OfferPracticeIcon />;
}

function PricingAccordionIcon({ open }: { open: boolean }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="100%" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3.75 12H20.25" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {!open ? (
        <path d="M12 3.75V20.25" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      ) : null}
    </svg>
  );
}

function PricingFeatureCheckIcon() {
  return (
    <svg width="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="currentColor" />
      <path d="M8 12l2.5 2.5L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PricingLightbulbIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M9 18h6M10 21h4M12 2a7 7 0 0 0-7 7c0 2.38 1.19 4.47 3 5.74V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.26c1.81-1.27 3-3.36 3-5.74a7 7 0 0 0-7-7Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ProcessEyebrowIcon() {
  return (
    <svg width="100%" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        opacity="0.2"
        d="M14.1981 11.1472L10.3246 12.5746L8.89724 16.4481C8.85782 16.5549 8.78661 16.6471 8.6932 16.7122C8.59979 16.7772 8.48867 16.8121 8.37482 16.8121C8.26097 16.8121 8.14986 16.7772 8.05645 16.7122C7.96304 16.6471 7.89182 16.5549 7.8524 16.4481L6.42506 12.5746L2.55154 11.1472C2.44473 11.1078 2.35258 11.0366 2.28749 10.9432C2.2224 10.8498 2.1875 10.7387 2.1875 10.6248C2.1875 10.511 2.2224 10.3999 2.28749 10.3064C2.35258 10.213 2.44473 10.1418 2.55154 10.1024L6.42506 8.67506L7.8524 4.80154C7.89182 4.69473 7.96304 4.60258 8.05645 4.53749C8.14986 4.4724 8.26097 4.4375 8.37482 4.4375C8.48867 4.4375 8.59979 4.4724 8.6932 4.53749C8.78661 4.60258 8.85782 4.69473 8.89724 4.80154L10.3246 8.67506L14.1981 10.1024C14.3049 10.1418 14.3971 10.213 14.4622 10.3064C14.5272 10.3999 14.5621 10.511 14.5621 10.6248C14.5621 10.7387 14.5272 10.8498 14.4622 10.9432C14.3971 11.0366 14.3049 11.1078 14.1981 11.1472Z"
        fill="currentColor"
      />
      <path
        d="M14.3952 9.57453L10.7685 8.23437L9.43255 4.60484C9.35343 4.38992 9.2103 4.20443 9.02245 4.07341C8.83461 3.94239 8.6111 3.87214 8.38208 3.87214C8.15305 3.87214 7.92954 3.94239 7.7417 4.07341C7.55386 4.20443 7.41072 4.38992 7.33161 4.60484L5.98723 8.23437L2.3577 9.57031C2.14278 9.64942 1.95729 9.79256 1.82627 9.9804C1.69525 10.1682 1.625 10.3918 1.625 10.6208C1.625 10.8498 1.69525 11.0733 1.82627 11.2612C1.95729 11.449 2.14278 11.5921 2.3577 11.6712L5.98723 13.0156L7.32317 16.6452C7.40228 16.8601 7.54542 17.0456 7.73326 17.1766C7.9211 17.3076 8.14462 17.3779 8.37364 17.3779C8.60266 17.3779 8.82617 17.3076 9.01402 17.1766C9.20186 17.0456 9.34499 16.8601 9.42411 16.6452L10.7685 13.0156L14.398 11.6797C14.6129 11.6006 14.7984 11.4574 14.9294 11.2696C15.0605 11.0818 15.1307 10.8582 15.1307 10.6292C15.1307 10.4002 15.0605 10.1767 14.9294 9.98884C14.7984 9.801 14.6129 9.65786 14.398 9.57875L14.3952 9.57453ZM10.1357 12.0467C10.0593 12.0749 9.98998 12.1192 9.93244 12.1768C9.8749 12.2343 9.83053 12.3036 9.80239 12.38L8.37786 16.2395L6.95614 12.3828C6.92804 12.3057 6.8834 12.2356 6.82534 12.1775C6.76727 12.1195 6.6972 12.0748 6.62005 12.0467L2.76341 10.625L6.62005 9.20328C6.6972 9.17519 6.76727 9.13054 6.82534 9.07248C6.8834 9.01441 6.92804 8.94434 6.95614 8.86719L8.37786 5.01055L9.79958 8.86719C9.82772 8.94354 9.87209 9.01288 9.92963 9.07042C9.98717 9.12796 10.0565 9.17233 10.1329 9.20047L13.9923 10.625L10.1357 12.0467ZM10.6279 3.3125C10.6279 3.16332 10.6871 3.02024 10.7926 2.91475C10.8981 2.80926 11.0412 2.75 11.1904 2.75H12.3154V1.625C12.3154 1.47582 12.3746 1.33274 12.4801 1.22725C12.5856 1.12176 12.7287 1.0625 12.8779 1.0625C13.027 1.0625 13.1701 1.12176 13.2756 1.22725C13.3811 1.33274 13.4404 1.47582 13.4404 1.625V2.75H14.5654C14.7145 2.75 14.8576 2.80926 14.9631 2.91475C15.0686 3.02024 15.1279 3.16332 15.1279 3.3125C15.1279 3.46168 15.0686 3.60476 14.9631 3.71025C14.8576 3.81574 14.7145 3.875 14.5654 3.875H13.4404V5C13.4404 5.14918 13.3811 5.29226 13.2756 5.39775C13.1701 5.50324 13.027 5.5625 12.8779 5.5625C12.7287 5.5625 12.5856 5.50324 12.4801 5.39775C12.3746 5.29226 12.3154 5.14918 12.3154 5V3.875H11.1904C11.0412 3.875 10.8981 3.81574 10.7926 3.71025C10.6871 3.60476 10.6279 3.46168 10.6279 3.3125ZM17.9404 6.6875C17.9404 6.83668 17.8811 6.97976 17.7756 7.08525C17.6701 7.19074 17.527 7.25 17.3779 7.25H16.8154V7.8125C16.8154 7.96168 16.7561 8.10476 16.6506 8.21025C16.5451 8.31574 16.402 8.375 16.2529 8.375C16.1037 8.375 15.9606 8.31574 15.8551 8.21025C15.7496 8.10476 15.6904 7.96168 15.6904 7.8125V7.25H15.1279C14.9787 7.25 14.8356 7.19074 14.7301 7.08525C14.6246 6.97976 14.5654 6.83668 14.5654 6.6875C14.5654 6.53832 14.6246 6.39524 14.7301 6.28975C14.8356 6.18426 14.9787 6.125 15.1279 6.125H15.6904V5.5625C15.6904 5.41332 15.7496 5.27024 15.8551 5.16475C15.9606 5.05926 16.1037 5 16.2529 5C16.402 5 16.5451 5.05926 16.6506 5.16475C16.7561 5.27024 16.8154 5.41332 16.8154 5.5625V6.125H17.3779C17.527 6.125 17.6701 6.18426 17.7756 6.28975C17.8811 6.39524 17.9404 6.53832 17.9404 6.6875Z"
        fill="currentColor"
      />
    </svg>
  );
}

function ProcessLibraryIcon() {
  return (
    <svg width="100%" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M28 16.0001V26.0001C28 26.5305 27.7893 27.0392 27.4142 27.4143C27.0391 27.7893 26.5304 28.0001 26 28.0001H6C5.46957 28.0001 4.96086 27.7893 4.58579 27.4143C4.21071 27.0392 4 26.5305 4 26.0001V6.00005C4 5.46962 4.21071 4.96091 4.58579 4.58584C4.96086 4.21077 5.46957 4.00005 6 4.00005H16C16.2652 4.00005 16.5196 4.10541 16.7071 4.29295C16.8946 4.48048 17 4.73484 17 5.00005C17 5.26527 16.8946 5.51962 16.7071 5.70716C16.5196 5.8947 16.2652 6.00005 16 6.00005H6V26.0001H26V16.0001C26 15.7348 26.1054 15.4805 26.2929 15.2929C26.4804 15.1054 26.7348 15.0001 27 15.0001C27.2652 15.0001 27.5196 15.1054 27.7071 15.2929C27.8946 15.4805 28 15.7348 28 16.0001ZM28.7075 8.70755L16.7075 20.7076C16.6146 20.8004 16.5042 20.874 16.3829 20.9242C16.2615 20.9744 16.1314 21.0002 16 21.0001H12C11.7348 21.0001 11.4804 20.8947 11.2929 20.7072C11.1054 20.5196 11 20.2653 11 20.0001V16.0001C10.9999 15.8687 11.0257 15.7386 11.0759 15.6172C11.1261 15.4958 11.1997 15.3855 11.2925 15.2926L23.2925 3.29255C23.3854 3.19958 23.4957 3.12582 23.6171 3.07549C23.7385 3.02517 23.8686 2.99927 24 2.99927C24.1314 2.99927 24.2615 3.02517 24.3829 3.07549C24.5043 3.12582 24.6146 3.19958 24.7075 3.29255L28.7075 7.29255C28.8005 7.38543 28.8742 7.49572 28.9246 7.61711C28.9749 7.73851 29.0008 7.86864 29.0008 8.00005C29.0008 8.13147 28.9749 8.2616 28.9246 8.38299C28.8742 8.50439 28.8005 8.61468 28.7075 8.70755ZM26.5825 8.00005L24 5.4138L22.4137 7.00005L25 9.5863L26.5825 8.00005Z"
        fill="currentColor"
      />
    </svg>
  );
}

function ProcessPerformanceIcon() {
  return (
    <svg width="100%" viewBox="0 0 32 33" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M24 3.5H12C11.4696 3.5 10.9609 3.71071 10.5858 4.08579C10.2107 4.46086 10 4.96957 10 5.5V7.5H8C7.46957 7.5 6.96086 7.71071 6.58579 8.08579C6.21071 8.46086 6 8.96957 6 9.5V28.5C6 28.6837 6.05061 28.8639 6.1463 29.0208C6.24199 29.1776 6.37904 29.3051 6.54241 29.3892C6.70579 29.4733 6.88917 29.5107 7.07243 29.4974C7.25569 29.4841 7.43174 29.4205 7.58125 29.3137L14 24.7288L20.42 29.3137C20.5695 29.4202 20.7454 29.4835 20.9285 29.4966C21.1116 29.5097 21.2947 29.4722 21.4579 29.3882C21.621 29.3041 21.7579 29.1768 21.8536 29.0201C21.9492 28.8635 21.9998 28.6835 22 28.5V23.5863L24.4188 25.3137C24.5683 25.4205 24.7443 25.4841 24.9276 25.4974C25.1108 25.5107 25.2942 25.4733 25.4576 25.3892C25.621 25.3051 25.758 25.1776 25.8537 25.0208C25.9494 24.8639 26 24.6837 26 24.5V5.5C26 4.96957 25.7893 4.46086 25.4142 4.08579C25.0391 3.71071 24.5304 3.5 24 3.5ZM24 22.5575L22 21.125V9.5C22 8.96957 21.7893 8.46086 21.4142 8.08579C21.0391 7.71071 20.5304 7.5 20 7.5H12V5.5H24V22.5575Z"
        fill="currentColor"
      />
    </svg>
  );
}

function ProcessSharingIcon() {
  return (
    <svg width="100%" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M30.0007 11.0285C29.9505 12.7693 29.2347 14.4246 28.0007 15.6535L23.657 19.9997C23.0243 20.6358 22.2718 21.1401 21.4429 21.4834C20.6141 21.8267 19.7254 22.0022 18.8282 21.9997H18.822C17.9095 21.9991 17.0064 21.8156 16.1661 21.46C15.3257 21.1045 14.5652 20.5842 13.9293 19.9298C13.2934 19.2754 12.7951 18.5002 12.4639 17.65C12.1327 16.7998 11.9752 15.8918 12.0007 14.9797C12.0082 14.7145 12.1207 14.4631 12.3135 14.2808C12.5063 14.0986 12.7636 14.0004 13.0288 14.0078C13.2941 14.0153 13.5454 14.1278 13.7277 14.3206C13.91 14.5134 14.0082 14.7707 14.0007 15.036C13.9825 15.6809 14.0937 16.3229 14.3279 16.9241C14.562 17.5252 14.9143 18.0734 15.3638 18.5361C15.8134 18.9989 16.3512 19.3668 16.9453 19.6182C17.5395 19.8696 18.178 19.9993 18.8232 19.9997C19.4574 20.0013 20.0857 19.8772 20.6716 19.6346C21.2576 19.3919 21.7896 19.0355 22.237 18.586L26.5807 14.2422C27.4769 13.3347 27.9777 12.1095 27.9737 10.8341C27.9697 9.55866 27.4613 8.33662 26.5594 7.43475C25.6575 6.53289 24.4355 6.02446 23.1601 6.02047C21.8847 6.01648 20.6595 6.51725 19.752 7.41345L18.377 8.78845C18.1879 8.96807 17.9362 9.06673 17.6754 9.06339C17.4146 9.06005 17.1655 8.95498 16.9811 8.77058C16.7967 8.58618 16.6916 8.33704 16.6883 8.07627C16.6849 7.81551 16.7836 7.56376 16.9632 7.3747L18.3382 5.9997C18.9726 5.36511 19.7258 4.86172 20.5547 4.51827C21.3837 4.17482 22.2722 3.99805 23.1695 3.99805C24.0668 3.99805 24.9553 4.17482 25.7842 4.51827C26.6132 4.86172 27.3663 5.36511 28.0007 5.9997C28.6578 6.6584 29.1739 7.44391 29.5178 8.30843C29.8616 9.17296 30.0259 10.0984 30.0007 11.0285ZM13.6257 23.2072L12.2507 24.5822C11.8022 25.0337 11.2685 25.3915 10.6805 25.6348C10.0925 25.8782 9.46206 26.0022 8.82571 25.9997C7.87109 25.9989 6.93811 25.7152 6.14467 25.1844C5.35122 24.6536 4.73291 23.8996 4.36786 23.0175C4.0028 22.1354 3.90739 21.1649 4.09368 20.2287C4.27996 19.2924 4.73958 18.4324 5.41446 17.7572L9.75071 13.4135C10.4337 12.7269 11.3073 12.2615 12.2582 12.0778C13.2091 11.8941 14.1932 12.0006 15.0828 12.3834C15.9723 12.7661 16.7262 13.4076 17.2466 14.2243C17.767 15.0411 18.0297 15.9955 18.0007 16.9635C17.9933 17.2287 18.0915 17.486 18.2737 17.6788C18.456 17.8716 18.7074 17.9841 18.9726 17.9916C19.2378 17.999 19.4951 17.9008 19.6879 17.7186C19.8807 17.5363 19.9933 17.2849 20.0007 17.0197C20.0247 16.0911 19.8598 15.1673 19.516 14.3044C19.1722 13.4415 18.6567 12.6574 18.0007 11.9997C16.7197 10.7192 14.9826 9.99992 13.1713 9.99992C11.3601 9.99992 9.62299 10.7192 8.34196 11.9997L4.00071 16.3435C3.046 17.2978 2.39561 18.5137 2.13173 19.8375C1.86784 21.1614 2.00232 22.5338 2.51815 23.7812C3.03399 25.0286 3.90803 26.0952 5.02983 26.846C6.15162 27.5969 7.47082 27.9984 8.82071 27.9997C9.71805 28.0023 10.607 27.8269 11.4361 27.4836C12.2651 27.1402 13.0179 26.6359 13.6507 25.9997L15.0257 24.6247C15.1875 24.4341 15.2721 24.1897 15.2627 23.9399C15.2533 23.69 15.1506 23.4527 14.9749 23.2748C14.7992 23.0969 14.5632 22.9913 14.3134 22.9788C14.0637 22.9662 13.8183 23.0477 13.6257 23.2072Z"
        fill="currentColor"
      />
    </svg>
  );
}

function OfferTutorialIcon() {
  return (
    <svg width="100%" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M14.7028 5.77357L12.949 7.25357L13.484 9.46544C13.5228 9.62557 13.5133 9.79361 13.4567 9.94834C13.4001 10.1031 13.2989 10.2376 13.1659 10.3348C13.0313 10.4337 12.8705 10.4906 12.7037 10.4982C12.5369 10.5059 12.3715 10.4641 12.2284 10.3779L10.2503 9.18732L8.27215 10.3786C8.12915 10.4649 7.96374 10.5069 7.79686 10.4992C7.62998 10.4916 7.46913 10.4346 7.33465 10.3354C7.20142 10.2384 7.1 10.1039 7.04326 9.94916C6.98651 9.79439 6.97699 9.62625 7.0159 9.46607L7.5509 7.25419L5.79778 5.77357C5.67082 5.66625 5.5787 5.52356 5.53316 5.36368C5.48762 5.2038 5.49073 5.03398 5.54209 4.87587C5.59345 4.71776 5.69074 4.57854 5.82154 4.47594C5.95234 4.37335 6.11074 4.31203 6.27653 4.29982L8.58528 4.11732L9.4734 2.01357C9.53828 1.86122 9.64651 1.73131 9.78464 1.64C9.92277 1.54868 10.0847 1.5 10.2503 1.5C10.4159 1.5 10.5778 1.54868 10.7159 1.64C10.854 1.73131 10.9623 1.86122 11.0272 2.01357L11.9153 4.11732L14.224 4.29982C14.3898 4.31203 14.5482 4.37335 14.679 4.47594C14.8098 4.57854 14.9071 4.71776 14.9585 4.87587C15.0098 5.03398 15.0129 5.2038 14.9674 5.36368C14.9219 5.52356 14.8297 5.66625 14.7028 5.77357ZM5.50715 7.49294C5.46072 7.44645 5.40557 7.40957 5.34487 7.38441C5.28417 7.35925 5.21911 7.3463 5.1534 7.3463C5.0877 7.3463 5.02263 7.35925 4.96193 7.38441C4.90123 7.40957 4.84609 7.44645 4.79965 7.49294L1.14653 11.1461C1.05271 11.2399 1 11.3671 1 11.4998C1 11.6325 1.05271 11.7597 1.14653 11.8536C1.24035 11.9474 1.3676 12.0001 1.50028 12.0001C1.63296 12.0001 1.76021 11.9474 1.85403 11.8536L5.50715 8.20044C5.55364 8.15401 5.59052 8.09886 5.61568 8.03816C5.64085 7.97746 5.6538 7.9124 5.6538 7.84669C5.6538 7.78098 5.64085 7.71592 5.61568 7.65522C5.59052 7.59452 5.55364 7.53938 5.50715 7.49294ZM5.4759 11.3173L2.14653 14.6461C2.10007 14.6925 2.06322 14.7477 2.03808 14.8084C2.01294 14.8691 2 14.9341 2 14.9998C2 15.0655 2.01294 15.1306 2.03808 15.1913C2.06322 15.252 2.10007 15.3071 2.14653 15.3536C2.24035 15.4474 2.3676 15.5001 2.50028 15.5001C2.56598 15.5001 2.63103 15.4872 2.69173 15.462C2.75242 15.4369 2.80757 15.4 2.85403 15.3536L6.18278 12.0242C6.27259 11.9297 6.32192 11.8038 6.32025 11.6734C6.31858 11.543 6.26604 11.4185 6.17384 11.3263C6.08164 11.2341 5.95707 11.1815 5.82669 11.1798C5.69631 11.1782 5.57043 11.2275 5.4759 11.3173ZM10.0384 11.2548L6.64528 14.6473C6.59882 14.6938 6.56197 14.7489 6.53683 14.8096C6.51169 14.8703 6.49875 14.9354 6.49875 15.0011C6.49875 15.0668 6.51169 15.1318 6.53683 15.1925C6.56197 15.2532 6.59882 15.3084 6.64528 15.3548C6.7391 15.4486 6.86635 15.5013 6.99903 15.5013C7.06473 15.5013 7.12978 15.4884 7.19048 15.4633C7.25117 15.4381 7.30632 15.4013 7.35278 15.3548L10.7453 11.9623C10.8391 11.8686 10.8918 11.7414 10.8919 11.6088C10.892 11.4762 10.8393 11.3489 10.7456 11.2551C10.6519 11.1613 10.5247 11.1086 10.3921 11.1085C10.2594 11.1085 10.1322 11.1611 10.0384 11.2548Z" fill="currentColor" />
    </svg>
  );
}

function OfferPlayButtonIcon() {
  return (
    <svg width="100%" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M22.5 12.5005C22.5006 12.7551 22.4353 13.0056 22.3105 13.2275C22.1856 13.4495 22.0055 13.6353 21.7875 13.767L8.28 22.0302C8.05227 22.1696 7.79144 22.2457 7.52445 22.2507C7.25746 22.2556 6.99399 22.1892 6.76125 22.0583C6.53073 21.9294 6.3387 21.7414 6.2049 21.5137C6.07111 21.286 6.00039 21.0268 6 20.7627V4.23828C6.00039 3.97417 6.07111 3.71493 6.2049 3.48722C6.3387 3.25951 6.53073 3.07155 6.76125 2.94266C6.99399 2.81173 7.25746 2.74531 7.52445 2.75026C7.79144 2.7552 8.05227 2.83133 8.28 2.97078L21.7875 11.2339C22.0055 11.3656 22.1856 11.5515 22.3105 11.7734C22.4353 11.9953 22.5006 12.2458 22.5 12.5005Z" fill="currentColor" />
    </svg>
  );
}

function TypeCardArrowIcon({ className }: { className?: string }) {
  return (
    <svg
      width="100%"
      viewBox="0 0 15 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={className}
    >
      <path
        d="M9.02297 0.539219L14.0855 5.60172C14.1378 5.65396 14.1793 5.716 14.2076 5.78428C14.2359 5.85257 14.2504 5.92577 14.2504 5.99969C14.2504 6.07361 14.2359 6.14681 14.2076 6.21509C14.1793 6.28338 14.1378 6.34542 14.0855 6.39766L9.02297 11.4602C8.91742 11.5657 8.77427 11.625 8.625 11.625C8.47573 11.625 8.33258 11.5657 8.22703 11.4602C8.12148 11.3546 8.06219 11.2115 8.06219 11.0622C8.06219 10.9129 8.12148 10.7698 8.22703 10.6642L12.3298 6.56219L1.3125 6.56219C1.16332 6.56219 1.02024 6.50292 0.914753 6.39743C0.809264 6.29195 0.750001 6.14887 0.750001 5.99969C0.750001 5.8505 0.809264 5.70743 0.914753 5.60194C1.02024 5.49645 1.16332 5.43719 1.3125 5.43719L12.3298 5.43719L8.22703 1.33516C8.12148 1.22961 8.06219 1.08646 8.06219 0.937188C8.06219 0.78792 8.12148 0.644767 8.22703 0.539219C8.33258 0.433671 8.47573 0.374374 8.625 0.374374C8.77427 0.374374 8.91742 0.433671 9.02297 0.539219Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function LandingPage() {
  useSEO({
    title: "BollyRhythm - Our Moves. Your Dance.",
    description:
      "Choreographed Bollywood tutorials for all skill levels--perfect for South Asian events. Learn and create performances anytime, anywhere, and arrive confident.",
    canonical: "/",
    ogDescription: "Create unforgettable performances without the stress.",
  });

  const { data: dbVideos, loading: homepageVideosLoading } = useHomepageVideos();
  const { addToCart, cartItems, setCartOpen } = useAppStore();
  const heroVideos: HeroVideoSource[] = useMemo(() => {
    if (dbVideos && dbVideos.length > 0) {
      return dbVideos.map((v, i) => ({
        poster: v.poster_url ?? fallbackPosters[i] ?? fallbackPosters[0]!,
        src: v.video_url,
        mobileSrc: getCmsMobilePreviewSrc(v),
        playbackSrc: heroPlaybackSrcByPreviewSrc[v.video_url] ?? v.video_url,
      }));
    }

    return homepageVideosLoading ? loadingHeroVideos : defaultHeroVideos;
  }, [dbVideos, homepageVideosLoading]);

  useEffect(() => {
    const preloadLinks = heroVideos
      .map((video) => video.poster)
      .filter((poster) => poster && poster.startsWith("http"))
      .map((poster) => {
        const link = document.createElement("link");
        link.rel = "preload";
        link.href = poster;
        link.as = "image";
        document.head.appendChild(link);
        return link;
      });

    return () => {
      preloadLinks.forEach((link) => link.remove());
    };
  }, [heroVideos]);

  const heroCardsRef = useRef<HTMLDivElement | null>(null);
  const heroTriggerRef = useRef<HTMLDivElement | null>(null);
  const mobileHeroTouchStartXRef = useRef<number | null>(null);
  const [mobileHeroDragX, setMobileHeroDragX] = useState(0);
  const [isMobileHeroDragging, setIsMobileHeroDragging] = useState(false);
  const [isOfferVideoOpen, setIsOfferVideoOpen] = useState(false);
  const [activeMobileHeroIndex, setActiveMobileHeroIndex] = useState(0);
  const [activeMobilePreviewReady, setActiveMobilePreviewReady] = useState(false);
  const [loadAllMobilePreviews, setLoadAllMobilePreviews] = useState(false);
  const [openPricingPlans, setOpenPricingPlans] = useState<Set<PricingPlan["key"]>>(new Set());
  const [isDesktopHero, setIsDesktopHero] = useState(() =>
    typeof window === "undefined" ? true : window.matchMedia("(min-width: 992px)").matches,
  );
  const mobilePreviewSrcs = useMemo(
    () => Array.from(new Set(heroVideos.map(getHeroMobilePreviewSrc).filter((src): src is string => Boolean(src)))),
    [heroVideos],
  );
  const mobilePreviewKey = mobilePreviewSrcs.join("\u001f");
  const activeMobilePreviewSrc = isDesktopHero
    ? undefined
    : getHeroMobilePreviewSrc(heroVideos[activeMobileHeroIndex]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 992px)");
    const handleChange = () => setIsDesktopHero(mediaQuery.matches);

    handleChange();
    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  useEffect(() => {
    setActiveMobilePreviewReady(false);
  }, [activeMobilePreviewSrc]);

  useEffect(() => {
    setLoadAllMobilePreviews(false);
  }, [mobilePreviewKey]);

  useEffect(() => {
    if (isDesktopHero || !activeMobilePreviewSrc) {
      return;
    }

    const link = document.createElement("link");
    link.rel = "preload";
    link.href = activeMobilePreviewSrc;
    link.as = "video";
    link.type = "video/mp4";
    link.setAttribute("fetchpriority", "high");
    document.head.appendChild(link);

    return () => {
      link.remove();
    };
  }, [activeMobilePreviewSrc, isDesktopHero]);

  useEffect(() => {
    if (isDesktopHero || loadAllMobilePreviews || mobilePreviewSrcs.length <= 1) {
      return;
    }

    // Initial mobile load: only the visible card fetches first. After it is ready (or a short fallback),
    // mount the other two video elements too so they fetch and keep looping in the background.
    const preloadTimer = window.setTimeout(
      () => setLoadAllMobilePreviews(true),
      activeMobilePreviewReady ? 250 : 2500,
    );

    return () => {
      window.clearTimeout(preloadTimer);
    };
  }, [activeMobilePreviewReady, isDesktopHero, loadAllMobilePreviews, mobilePreviewSrcs.length]);

  const isVirtualPracticeInCart = cartItems.some(
    (item) => item.type === "add_on" && isSameAddOn(item, DEFAULT_VIRTUAL_PRACTICE_ADD_ON),
  );
  const isCustomMusicMixInCart = cartItems.some(
    (item) => item.type === "add_on" && isSameAddOn(item, DEFAULT_CUSTOM_MIX_ADD_ON),
  );

  const handleVirtualPracticeAddToCart = useCallback(() => {
    addToCart({
      type: "add_on",
      add_on_id: DEFAULT_VIRTUAL_PRACTICE_ADD_ON.id,
      name: DEFAULT_VIRTUAL_PRACTICE_ADD_ON.name,
      price: DEFAULT_VIRTUAL_PRACTICE_ADD_ON.price,
      image_url: DEFAULT_VIRTUAL_PRACTICE_ADD_ON.image_url ?? undefined,
    });
    setCartOpen(true);
  }, [addToCart, setCartOpen]);

  const handleCustomMusicMixAddToCart = useCallback(() => {
    addToCart({
      type: "add_on",
      add_on_id: DEFAULT_CUSTOM_MIX_ADD_ON.id,
      name: DEFAULT_CUSTOM_MIX_ADD_ON.name,
      price: DEFAULT_CUSTOM_MIX_ADD_ON.price,
      image_url: DEFAULT_CUSTOM_MIX_ADD_ON.image_url ?? undefined,
    });
    setCartOpen(true);
  }, [addToCart, setCartOpen]);

  const togglePricingPlan = useCallback((planKey: PricingPlan["key"]) => {
    setOpenPricingPlans((current) => {
      const next = new Set(current);
      if (next.has(planKey)) {
        next.delete(planKey);
      } else {
        next.add(planKey);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const heroCards = heroCardsRef.current;
    const heroTrigger = heroTriggerRef.current;

    if (!heroCards || !heroTrigger) {
      return;
    }

    let frameId = 0;
    const triggerDocumentTop = heroTrigger.getBoundingClientRect().top + window.scrollY;

    const updateHeroCards = () => {
      frameId = 0;

      if (window.innerWidth < 992) {
        heroCards.style.setProperty("--hero-card-progress", "0");
        return;
      }

      const triggerRect = heroTrigger.getBoundingClientRect();
      const normalizedTop = triggerRect.top - triggerDocumentTop;
      const endTop = window.innerHeight / 2 - triggerRect.height;
      const scrollDistance = Math.abs(endTop);

      let progress = 0;

      if (scrollDistance > 0 && normalizedTop < 0) {
        progress = Math.min(Math.max(-normalizedTop / scrollDistance, 0), 1);
      }

      const easedProgress = 1 - (1 - progress) * (1 - progress);
      heroCards.style.setProperty("--hero-card-progress", easedProgress.toFixed(4));
    };

    const requestUpdate = () => {
      if (frameId !== 0) {
        return;
      }

      frameId = window.requestAnimationFrame(updateHeroCards);
    };

    updateHeroCards();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);

    return () => {
      if (frameId !== 0) {
        window.cancelAnimationFrame(frameId);
      }

      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
      heroCards.style.removeProperty("--hero-card-progress");
    };
  }, []);

  useEffect(() => {
    if (!isOfferVideoOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOfferVideoOpen(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOfferVideoOpen]);

  const showNextMobileHeroCard = () => {
    setActiveMobileHeroIndex((currentIndex) => (currentIndex + 1) % heroVideos.length);
  };

  const showPreviousMobileHeroCard = () => {
    setActiveMobileHeroIndex((currentIndex) => (currentIndex - 1 + heroVideos.length) % heroVideos.length);
  };

  const handleMobileHeroTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    mobileHeroTouchStartXRef.current = event.touches[0]?.clientX ?? null;
    setIsMobileHeroDragging(true);
    setMobileHeroDragX(0);
  };

  const handleMobileHeroTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    const startX = mobileHeroTouchStartXRef.current;
    const currentX = event.touches[0]?.clientX;
    if (startX == null || currentX == null) return;
    setMobileHeroDragX(currentX - startX);
  };

  const handleMobileHeroTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    const startX = mobileHeroTouchStartXRef.current;
    const endX = event.changedTouches[0]?.clientX;

    mobileHeroTouchStartXRef.current = null;
    setIsMobileHeroDragging(false);
    setMobileHeroDragX(0);

    if (startX == null || endX == null) {
      return;
    }

    const deltaX = endX - startX;

    if (Math.abs(deltaX) < 40) {
      return;
    }

    if (deltaX < 0) {
      showNextMobileHeroCard();
      return;
    }

    showPreviousMobileHeroCard();
  };

  const handleActiveMobilePreviewReady = useCallback(() => {
    setActiveMobilePreviewReady(true);
  }, []);

  return (
    <div className="min-h-screen overflow-x-clip bg-background-primary text-[#1f0a18]">
      <Navbar />

      <main className="relative isolate">
        {/* ═══ HERO ═══ */}
        <section className="relative [clip-path:polygon(0_-1000%,100%_-1000%,100%_1000%,0_1000%)] overflow-clip bg-transparent">
          <div className="px-[var(--site-margin)]">
            <div className="mx-auto w-full max-w-[80rem]">
              <div className="py-[6.25rem] max-[767px]:py-16">
                <div className="relative z-[2] mx-auto flex w-full flex-col items-center text-center">
                  <div className="mx-auto flex max-w-[6ch] text-[clamp(2.5rem,2.0714rem+2.1429vw,4rem)] [mask-image:linear-gradient(transparent,#000)] [-webkit-mask-image:linear-gradient(transparent,#000)]">
                    <svg width="100%" viewBox="0 0 241 49" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 0V49H0V0H1ZM41 0V49H40V0H41ZM81 0V49H80V0H81ZM121 0V49H120V0H121ZM161 0V49H160V0H161ZM201 0V49H200V0H201ZM241 0V49H240V0H241ZM9 0V26H8V0H9ZM17 0V26H16V0H17ZM25 0V26H24V0H25ZM33 0V26H32V0H33ZM49 0V26H48V0H49ZM57 0V26H56V0H57ZM65 0V26H64V0H65ZM73 0V26H72V0H73ZM89 0V26H88V0H89ZM97 0V26H96V0H97ZM105 0V26H104V0H105ZM113 0V26H112V0H113ZM129 0V26H128V0H129ZM137 0V26H136V0H137ZM145 0V26H144V0H145ZM153 0V26H152V0H153ZM169 0V26H168V0H169ZM177 0V26H176V0H177ZM185 0V26H184V0H185ZM193 0V26H192V0H193ZM209 0V26H208V0H209ZM217 0V26H216V0H217ZM225 0V26H224V0H225ZM233 0V26H232V0H233Z" fill="currentColor" />
                    </svg>
                  </div>
                  <div className="w-full pt-6 max-[767px]:pt-5" />
                  <h1 className="m-0 font-display text-[clamp(2.5rem,2.0714rem+2.1429vw,4rem)] font-medium leading-none text-[#1f0a18]">
                    Our Moves
                    <br />
                    <span className="text-pink-dark">Your Dance</span>
                  </h1>
                  <div className="w-full pt-6 max-[767px]:pt-5" />
                  <p className="mx-auto my-0 w-[min(100%,45ch)] max-w-none text-[clamp(1rem,0.9643rem+0.1786vw,1.125rem)] font-medium leading-normal text-neutral-dark">
                    BollyRhythm is a virtual dance studio designed to help you create unforgettable solo or group
                    performances, no matter your experience or the occasion.
                  </p>
                  <div className="w-full pt-8 max-[991px]:pt-6 max-[767px]:pt-5" />
                  <div className="min-[768px]:translate-y-6">
                    <Link to="/dance-library" className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-large border-0 bg-pink-dark px-8 py-4 text-center text-[clamp(1rem,0.9643rem+0.1786vw,1.125rem)] font-medium leading-none text-white no-underline transition-transform duration-200 ease-[cubic-bezier(.39,.575,.565,1)] hover:-translate-y-[5%] active:scale-95" data-testid="landing-primary-cta">
                      Browse our tutorials
                    </Link>
                  </div>
                </div>

                {isDesktopHero && (
                <div className="relative mx-auto mt-[1.88rem] hidden w-full min-h-[33.125rem] items-end justify-center gap-10 max-[991px]:min-h-auto max-[767px]:gap-4 min-[992px]:flex">
                  <div className="absolute top-0 -z-[5] flex w-[110%] items-start justify-center -translate-y-[20%] max-[991px]:w-[130vw] max-[767px]:w-[180vw] max-[479px]:w-[290vw]" aria-hidden="true">
                    <HeroArc />
                  </div>

                  <div className="flex w-full justify-center !overflow-visible" aria-hidden="true">
                    <div ref={heroCardsRef} className="[--hero-card-progress:0] mt-auto flex w-[min(100%,74.375rem)] items-end justify-center gap-10 pt-[1.88rem] max-[991px]:w-[min(100%,52.25rem)] max-[991px]:gap-10 max-[991px]:pt-0 max-[767px]:gap-4">
                      {heroVideos.map((video, i) => (
                        <HeroVideoCard
                          key={i}
                          video={video}
                          className={cn(
                            "will-change-transform max-[991px]:w-[min(17.5rem,28vw)] max-[767px]:w-[min(17.5rem,29vw)] max-[479px]:w-[min(17rem,30vw)]",
                            i === 1
                              ? "z-[5] aspect-[3.7/5] w-[23.125rem] [--hero-card-offset-x:0px] [--hero-card-offset-y:0px] [--hero-card-start-scale:1] [--hero-card-start-rotate:0deg] [transform:translate3d(calc(var(--hero-card-offset-x)*(1-var(--hero-card-progress))),var(--hero-card-offset-y),0)_scale(calc(var(--hero-card-start-scale)+((1-var(--hero-card-start-scale))*var(--hero-card-progress))))_rotate(calc(var(--hero-card-start-rotate)*(1-var(--hero-card-progress))))] max-[991px]:z-auto max-[991px]:aspect-[3.7/4.7]"
                              : i === 0
                                ? "aspect-[3.7/4.7] w-[23.125rem] origin-[100%_0] [--hero-card-offset-x:82.7939px] [--hero-card-offset-y:-8.70199px] [--hero-card-start-scale:0.9] [--hero-card-start-rotate:-6deg] [transform:translate3d(calc(var(--hero-card-offset-x)*(1-var(--hero-card-progress))),var(--hero-card-offset-y),0)_scale(calc(var(--hero-card-start-scale)+((1-var(--hero-card-start-scale))*var(--hero-card-progress))))_rotate(calc(var(--hero-card-start-rotate)*(1-var(--hero-card-progress))))] max-[991px]:!transform-none"
                                : "aspect-[3.7/4.7] w-[23.125rem] origin-[0_0] [--hero-card-offset-x:-82.7939px] [--hero-card-offset-y:-8.70199px] [--hero-card-start-scale:0.9] [--hero-card-start-rotate:6deg] [transform:translate3d(calc(var(--hero-card-offset-x)*(1-var(--hero-card-progress))),var(--hero-card-offset-y),0)_scale(calc(var(--hero-card-start-scale)+((1-var(--hero-card-start-scale))*var(--hero-card-progress))))_rotate(calc(var(--hero-card-start-rotate)*(1-var(--hero-card-progress))))] max-[991px]:!transform-none"
                          )}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="absolute top-0 -z-[2] aspect-square h-72 rounded-full bg-yellow-brand blur-[220px]" aria-hidden="true" />

                  <div className="pointer-events-none absolute left-0 right-auto top-0 flex -translate-y-full flex-col items-center justify-start text-center font-[Bradhi,Georgia,sans-serif] font-normal text-[#4a4a4a] max-[991px]:hidden max-[479px]:hidden" aria-hidden="true">
                    <div>
                      <strong className="font-semibold">
                        Learn at your
                        <br />
                        own pace
                      </strong>
                    </div>
                    <div className="w-[clamp(1rem,0.9754rem+1.9718vw,2.75rem)] [transform-style:preserve-3d] -rotate-[20deg]">
                      <HeroArrowLeft />
                    </div>
                  </div>

                  <div className="pointer-events-none absolute right-0 left-auto top-0 flex -translate-y-full flex-col items-center justify-start text-center font-[Bradhi,Georgia,sans-serif] font-normal text-[#4a4a4a] max-[991px]:hidden max-[479px]:hidden" aria-hidden="true">
                    <div>
                      <strong className="font-semibold">
                        Practice anytime,
                        <br />
                        anywhere
                      </strong>
                    </div>
                    <div className="w-[clamp(1rem,0.9754rem+1.9718vw,2.75rem)] [transform-style:preserve-3d] rotate-[20deg]">
                      <HeroArrowRight />
                    </div>
                  </div>
                </div>
                )}

                {!isDesktopHero && (
                <div className="relative mx-auto mt-10 w-full min-[992px]:hidden">
                  <div className="absolute inset-x-0 top-0 -z-[4] flex items-start justify-center -translate-y-[16%]" aria-hidden="true">
                    <div className="w-[150vw] max-w-none text-[#f2a435] opacity-90 max-[767px]:w-[190vw] max-[479px]:w-[260vw]">
                      <HeroArc />
                    </div>
                  </div>

                  <div className="relative z-[2] mx-auto flex w-full max-w-[18.75rem] justify-center px-1 pb-6 pt-4 max-[479px]:max-w-[17.5rem]">
                    <div
                      className="relative aspect-[3.7/4.7] w-full touch-pan-y select-none overflow-visible"
                      onTouchStart={handleMobileHeroTouchStart}
                      onTouchMove={handleMobileHeroTouchMove}
                      onTouchEnd={handleMobileHeroTouchEnd}
                      aria-label="Tutorial preview cards"
                    >
                      {heroVideos.map((video, index) => {
                        const forwardIndex =
                          (index - activeMobileHeroIndex + heroVideos.length) % heroVideos.length;

                        const isTopCard = forwardIndex === 0;
                        const shouldLoadPreview = isTopCard || loadAllMobilePreviews;
                        const dragOffset = isTopCard && isMobileHeroDragging ? mobileHeroDragX : 0;
                        const dragRotate = isTopCard && isMobileHeroDragging ? (mobileHeroDragX / 300) * -5 : 0;

                        const baseRotate = isTopCard ? -7 : forwardIndex === 1 ? 2 : 5;
                        const baseX = isTopCard ? "-50% - 1rem" : forwardIndex === 1 ? "-50% + 0.45rem" : "-50% + 1.35rem";
                        const baseZ = isTopCard ? 30 : forwardIndex === 1 ? 20 : 10;

                        const stackStyle = {
                          transform: `translate3d(calc(${baseX} + ${dragOffset}px), 0, 0) rotate(${baseRotate + dragRotate}deg) scale(1)`,
                          zIndex: baseZ,
                        };

                        return (
                          <div
                            key={video.src || `${video.poster}-${index}`}
                            className={cn(
                              "absolute top-0 left-1/2 h-full w-[84%] overflow-hidden rounded-[1.45rem]",
                              isTopCard && isMobileHeroDragging
                                ? "transition-none"
                                : "transition-transform duration-500 ease-[cubic-bezier(.22,1,.36,1)]"
                            )}
                            style={stackStyle}
                            aria-hidden={!isTopCard}
                          >
                            <HeroVideoCard
                              video={video}
                              isVisible={isTopCard}
                              loadPreview={shouldLoadPreview}
                              previewSrc={getHeroMobilePreviewSrc(video)}
                              previewPreload={shouldLoadPreview ? "auto" : "none"}
                              previewAutoPlay={isTopCard}
                              showPlayOverlay={isTopCard}
                              onPreviewReady={isTopCard ? handleActiveMobilePreviewReady : undefined}
                              className={cn(
                                "h-full w-full rounded-[1.45rem]",
                                forwardIndex === 0
                                  ? "shadow-[inset_0_0_0_3px_#ffffff,0_26px_48px_rgba(74,25,60,0.14)]"
                                  : forwardIndex === 1
                                    ? "shadow-[inset_0_0_0_3px_#ffffff,0_18px_36px_rgba(74,25,60,0.12)]"
                                    : "shadow-[inset_0_0_0_3px_#ffffff,0_14px_28px_rgba(74,25,60,0.1)]",
                              )}
                              playButtonClassName="w-[4.25rem] max-[479px]:w-16"
                              playIconClassName="w-6 max-[479px]:w-5"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                )}
              </div>
            </div>
          </div>
          <div ref={heroTriggerRef} className="pointer-events-none absolute inset-[0%_auto_auto_0%] h-screen w-0" aria-hidden="true" />
        </section>

        {/* ═══ OUR OFFERING — What's Inside Each Tutorial ═══ */}
        <section id="about" className="relative scroll-mt-24 bg-transparent">
          <div className="px-[var(--site-margin)]">
            <div className="mx-auto w-full max-w-[80rem]">
              <div className="py-[6.25rem] max-[767px]:py-16">
                <div className="grid grid-cols-[minmax(0,36rem)_minmax(0,40rem)] justify-between gap-16 max-[991px]:flex max-[991px]:flex-col max-[991px]:gap-12 max-[767px]:gap-10">
                  <div className="flex flex-col max-[991px]:text-center">
                    <div className="max-[991px]:mx-auto">
                      <div className="flex w-fit justify-start rounded-full border-[0.09rem] border-[rgb(118_42_92_/_10%)] bg-[color-mix(in_srgb,#ffffff_40%,transparent)] p-[0.38rem] font-semibold text-pink-dark">
                        <div className="flex items-center gap-1 rounded-[inherit] border-[inherit] bg-white px-[0.88rem] py-2">
                          <div className="flex w-5 flex-col">
                            <OfferEyebrowIcon />
                          </div>
                          <div className="m-0 text-[0.88rem] font-semibold leading-none text-inherit">Our Offering</div>
                        </div>
                      </div>
                    </div>
                    <div className="w-full pt-6 max-[767px]:pt-5" />
                    <h2 className="m-0 font-display text-[clamp(3rem,2.7143rem+1.4286vw,3.5rem)] font-medium leading-[1.15] text-[#1f0a18]">
                      What&apos;s Inside
                      <br />
                      <span className="text-pink-dark">Each Tutorial</span>
                    </h2>
                    <div className="w-full pt-6 max-[767px]:pt-5" />
                    <p className="m-0 max-w-none text-[clamp(1rem,0.9643rem+0.1786vw,1.125rem)] font-medium leading-normal text-[#4a4a4a]">
                      Learn at your pace, practice with ease, and perform with confidence
                    </p>

                    <div className="mt-auto grid grid-cols-2 gap-8 pt-20 max-[991px]:pt-14 max-[767px]:grid-cols-1 max-[767px]:gap-8 max-[767px]:pt-10">
                      {offeringCards.map((item) => {
                        const Icon = item.icon;
                        return (
                          <div key={item.title} className="flex flex-col">
                            <div className="flex aspect-square w-[clamp(2.5rem,2.2143rem+1.4286vw,3.5rem)] min-w-[clamp(2.5rem,2.2143rem+1.4286vw,3.5rem)] min-h-[clamp(2.5rem,2.2143rem+1.4286vw,3.5rem)] shrink-0 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--color-pink-dark)_25%,transparent)] text-pink-dark max-[991px]:mx-auto">
                              <div className="flex w-[clamp(1.75rem,1.6786rem+0.3571vw,2rem)]">
                                <Icon />
                              </div>
                            </div>
                            <div className="w-full pt-6 max-[767px]:pt-5" />
                            <h3 className="m-0 font-display text-[clamp(1.25rem,1.0357rem+1.0714vw,2rem)] font-semibold leading-none text-[#1f0a18]">{item.title}</h3>
                            <div className="w-full pt-2" />
                            <p className="m-0 text-[clamp(1rem,0.9643rem+0.1786vw,1.125rem)] font-medium leading-normal text-[#4a4a4a]">{item.description}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="relative isolate flex aspect-square flex-col items-center justify-center rounded-[2.25rem] before:content-[''] before:absolute before:inset-0 before:z-[1] before:pointer-events-none before:rounded-[inherit] before:shadow-[inset_0_0_0_12px_var(--color-yellow-brand),inset_0_0_0_24px_#ffffff] max-[991px]:mx-auto max-[991px]:w-[min(100%,40rem)] max-[767px]:w-full max-[767px]:rounded-3xl max-[767px]:before:shadow-[inset_0_0_0_6px_var(--color-yellow-brand),inset_0_0_0_12px_#ffffff]">
                    <div className="absolute left-0 right-0 top-0 z-[2] mx-auto w-min -translate-y-[23%] whitespace-nowrap max-[767px]:-translate-y-1/2">
                      <div className="flex items-center gap-1 rounded-full bg-white px-[0.88rem] py-2 text-[0.88rem] text-yellow-brand shadow-[0_0_0_6px_#f9f6f3,inset_0_0_0_1px_rgb(255_167_36_/_10%)]">
                        <div className="flex w-4 shrink-0">
                          <OfferTutorialIcon />
                        </div>
                        <div className="text-sm">Video Tutorial</div>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="absolute z-[2] flex cursor-pointer items-center gap-2 rounded-full border-0 bg-[color-mix(in_srgb,#000000_40%,transparent)] px-6 py-4 text-[clamp(1rem,0.9643rem+0.1786vw,1.125rem)] font-medium leading-normal text-white backdrop-blur-[5px] max-[767px]:px-4 max-[767px]:py-2 max-[767px]:text-[0.88rem]"
                      onClick={() => setIsOfferVideoOpen(true)}
                    >
                      <div className="flex w-6 shrink-0 max-[767px]:w-4">
                        <OfferPlayButtonIcon />
                      </div>
                      <div>How it all works</div>
                    </button>

                    <div className="absolute inset-0 z-0 h-full w-full overflow-hidden rounded-[inherit]" aria-hidden="true">
                      <video
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        poster={offerPreviewVideo.poster}
                        className="absolute inset-0 block h-full w-full object-cover"
                      >
                        <source src={offerPreviewVideo.mp4} type="video/mp4" />
                        <source src={offerPreviewVideo.webm} type="video/webm" />
                      </video>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {isOfferVideoOpen ? (
            <div
              className="fixed inset-0 z-[70] flex items-center justify-center bg-[rgb(0_0_0_/_70%)] p-8 max-[767px]:p-4"
              role="dialog"
              aria-modal="true"
              aria-label="How it all works video"
              onClick={() => setIsOfferVideoOpen(false)}
            >
              <div className="relative aspect-video w-[min(100%,60rem)] max-[767px]:w-full" onClick={(event) => event.stopPropagation()}>
                <button
                  type="button"
                  className="absolute -top-4 -right-4 flex h-12 w-12 items-center justify-center rounded-full bg-[rgb(255_255_255_/_16%)] text-white backdrop-blur-[10px] max-[479px]:top-[-0.75rem] max-[479px]:right-0 max-[479px]:h-10 max-[479px]:w-10"
                  aria-label="Close video"
                  onClick={() => setIsOfferVideoOpen(false)}
                >
                  <svg width="100%" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M29.9988 30L18 18M18.0013 30L30 18" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M44 24C44 12.9543 35.0456 4 24 4C12.9543 4 4 12.9543 4 24C4 35.0456 12.9543 44 24 44C35.0456 44 44 35.0456 44 24Z" stroke="currentColor" strokeWidth="3" />
                  </svg>
                </button>
                <iframe
                  className="h-full w-full rounded-3xl border-0 bg-black shadow-[0_24px_60px_rgb(0_0_0_/_35%)]"
                  src={offerPreviewVideo.iframeSrc}
                  title="BollyRhythm - How it works"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            </div>
          ) : null}
        </section>

        {/* ═══ PRICING ═══ */}
        <section className="bg-transparent text-[#1f0a18] font-sans text-[clamp(1rem,0.9643rem+0.1786vw,1.125rem)] font-medium leading-normal">
          <div className="px-[var(--site-margin)]">
            <div className="mx-auto w-full max-w-[80rem]">
              <div className="py-[6.25rem] max-[767px]:py-16">
                <div className="text-center">
                  <div>
                    <div data-wf--eyebrow--variant="blue" className="mx-auto flex w-fit justify-start rounded-full border-[0.09rem] border-[rgb(118_42_92_/_10%)] bg-[color-mix(in_srgb,#ffffff_40%,transparent)] p-[0.38rem] font-semibold text-pink-dark">
                      <div className="flex items-center gap-1 rounded-[inherit] border-[inherit] bg-white px-[0.88rem] py-2">
                        <div className="flex w-5 flex-col">
                          <OfferEyebrowIcon />
                        </div>
                        <div className="m-0 text-[0.88rem] font-semibold leading-none text-inherit">Dance Experiences</div>
                      </div>
                    </div>
                    <div className="w-full pt-6 max-[767px]:pt-5" />
                    <h2 className="mx-auto m-0 max-w-[15ch] font-display text-[clamp(2rem,1.5714rem+2.1429vw,3.5rem)] font-medium leading-[1.15] text-[#1f0a18]">
                      Choose Your <span className="text-pink-dark">Dance Experience</span>
                    </h2>
                    <div className="w-full pt-6 max-[767px]:pt-5" />
                    <p className="mx-auto m-0 max-w-[50rem] text-balance text-[clamp(1rem,0.9643rem+0.1786vw,1.125rem)] font-medium leading-[1.6] text-[#5a5658]">
                      Whether you're dancing for fun, building your own routine, or preparing for a big performance - we've got you covered.
                    </p>
                    <div className="w-full pt-8 max-[767px]:pt-6" />
                    <div className="mx-auto flex w-full max-w-[44rem] items-start gap-4 rounded-[1.25rem] border border-[rgb(118_42_92_/_10%)] bg-[color-mix(in_srgb,#ffffff_88%,transparent)] p-5 text-left max-[767px]:gap-3 max-[767px]:p-4">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--color-yellow-brand)_22%,white)] text-yellow-brand max-[767px]:h-10 max-[767px]:w-10">
                        <PricingBestValueIcon className="w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold uppercase tracking-[0.18em] text-pink-dark/80">Not sure where to start?</div>
                        <p className="mt-2 mb-0 text-base leading-relaxed text-[#4a4a4a]">
                          Choose a Signature Routine for a complete, ready-to-perform experience.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-10 grid grid-cols-1 items-stretch gap-x-6 gap-y-12 md:grid-cols-2 xl:mt-28 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]">
                    {pricingPlans.map((plan) => {
                      const isOpen = openPricingPlans.has(plan.key);
                      const accordionId = `pricing-plan-${plan.key}-details`;
                      const centeredBadges = plan.badges.filter((badge) => centeredPricingBadges.has(badge));
                      const inlineBadges = plan.badges.filter((badge) => !centeredPricingBadges.has(badge));

                      return (
                        <article
                          key={plan.key}
                          className={cn(
                            "relative flex h-full flex-col rounded-[1.5rem] border border-[rgb(118_42_92_/_10%)] bg-white p-5 text-left shadow-[0_20px_55px_rgb(31_10_24_/_6%)] max-[479px]:p-4",
                            plan.orderClassName,
                            plan.featured
                              ? "xl:self-end xl:min-h-[calc(100%+4rem)] xl:pt-10 bg-[linear-gradient(180deg,#fff9f4_0%,#ffffff_34%,#fff7fb_100%)] shadow-[0_28px_75px_rgb(31_10_24_/_10%)] ring-1 ring-[rgb(255_167_36_/_40%)]"
                              : "",
                          )}
                        >
                          {centeredBadges.length > 0 ? (
                            <div className="absolute top-0 left-1/2 z-[1] flex max-w-[calc(100%-3rem)] -translate-x-1/2 -translate-y-1/2 flex-wrap justify-center gap-2 max-[767px]:static max-[767px]:mx-auto max-[767px]:mb-4 max-[767px]:translate-x-0 max-[767px]:translate-y-0">
                              {centeredBadges.map((badge) => (
                                <span
                                  key={`${plan.key}-centered-${badge}`}
                                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-white px-[0.88rem] py-2 text-[0.88rem] font-semibold text-pink-dark shadow-[0_0_0_6px_var(--color-background-primary),inset_0_0_0_1px_rgb(255_167_36_/_10%)]"
                                >
                                  <CelebrationIcon className="h-4 w-4 shrink-0" />
                                  <span>{badge}</span>
                                </span>
                              ))}
                            </div>
                          ) : null}

                          <div className={cn("flex h-full flex-col", plan.key === "build" ? "gap-4 max-[767px]:gap-3" : "gap-2 max-[767px]:gap-3")}>
                            <div className={cn("grid", plan.key === "sessions" ? "gap-3 lg:[grid-template-rows:minmax(3.75rem,auto)_minmax(4.5rem,auto)]" : "gap-2.5 lg:[grid-template-rows:minmax(4.75rem,auto)_minmax(4.5rem,auto)]")}>
                              <div className={cn(
                                "flex items-center gap-4",
                                plan.key === "sessions" ? "mt-4 lg:min-h-[3.75rem]" : "lg:min-h-[4.75rem]",
                                plan.key === "build" ? "mt-4" : "",
                              )}>
                                <div className="flex h-[3.5rem] w-[3.5rem] shrink-0 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--color-yellow-brand)_24%,white)] text-yellow-brand">
                                  <div className="flex w-7 shrink-0">
                                    <PricingPlanIcon planKey={plan.key} />
                                  </div>
                                </div>
                                <h3 className="m-0 font-display text-[clamp(1.4rem,1.1714rem+1.1429vw,2.2rem)] font-semibold leading-[1.15] text-[#1f0a18]">
                                  {plan.title}
                                </h3>
                              </div>
                              <p className="m-0 lg:min-h-[4.5rem] text-base leading-relaxed text-[#4a4a4a]">{plan.description}</p>
                            </div>

                            <div className={cn("rounded-[1rem] border border-[rgb(118_42_92_/_10%)] bg-[color-mix(in_srgb,#fff6f0_82%,white)] px-3.5 py-3 max-[767px]:mt-2", plan.key === "build" ? "md:-mt-2" : "")}>
                                <div className="text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-pink-dark/75">{plan.pricingHeading ?? "Pricing"}</div>
                                <div className="mt-2 flex items-end justify-between gap-3">
                                  <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1">
                                    <div className="font-display text-[clamp(1.85rem,1.7214rem+0.6429vw,2.3rem)] font-semibold leading-none tracking-[-0.03em] text-[#1f0a18]">
                                      {plan.displayPrice}
                                    </div>
                                    {plan.displayPriceNote ? (
                                      <div className="relative -top-0.5 whitespace-nowrap text-[0.9rem] font-medium leading-snug text-[#7b7277] max-[479px]:text-[0.86rem]">
                                        {plan.displayPriceNote}
                                      </div>
                                    ) : null}
                                  </div>

                                  {plan.pricingSideNote ? (
                                    <div className="w-[8.75rem] shrink-0 self-center text-center text-[0.9rem] font-medium leading-snug text-[#7b7277] max-[479px]:w-[8rem] max-[479px]:text-[0.86rem]">
                                      {plan.pricingSideNote}
                                    </div>
                                  ) : null}
                                </div>
                            </div>

                            <div className={cn("grid gap-2 lg:grid-cols-2 xl:grid-cols-1 xl:gap-2 max-[767px]:mt-3", plan.key === "build" ? "md:mt-1" : "md:mt-3")}>
                              <div className={cn("lg:min-h-[8.5rem]", plan.key === "build" ? "xl:min-h-0" : "")}>
                                <div className="text-[0.78rem] font-semibold uppercase tracking-[0.18em] text-pink-dark/75">Perfect for</div>
                                <ul role="list" className="mt-3 space-y-2.5 list-none pl-0">
                                  {plan.perfectFor.map((item) => (
                                    <li key={`${plan.key}-perfect-${item}`} className="mb-0">
                                      <div className="flex items-start gap-3">
                                        <div className="mt-0.5 flex w-5 shrink-0 text-pink-dark">
                                          <PricingFeatureCheckIcon />
                                        </div>
                                        <div className="text-sm leading-relaxed text-[#4a4a4a]">{item}</div>
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              </div>

                              <div className={cn("lg:min-h-[8.5rem] max-[767px]:mt-3", plan.key === "build" ? "lg:-mt-4 xl:mt-4" : "")}>
                                <div className="text-[0.78rem] font-semibold uppercase tracking-[0.18em] text-pink-dark/75">What's included</div>
                                <ul role="list" className="mt-3 space-y-2.5 list-none pl-0">
                                  {plan.topIncluded.map((item) => (
                                    <li key={`${plan.key}-top-${item}`} className="mb-0">
                                      <div className="flex items-start gap-3">
                                        <div className="mt-0.5 flex w-5 shrink-0 text-pink-dark">
                                          <PricingFeatureCheckIcon />
                                        </div>
                                        <div className="text-sm leading-relaxed text-[#4a4a4a]">{item}</div>
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>

                            <div className={cn("rounded-[1.25rem] border border-[rgb(118_42_92_/_10%)] bg-[color-mix(in_srgb,#ffffff_92%,transparent)] px-4 py-3.5 max-[767px]:my-3 max-[767px]:py-2.5 max-[479px]:px-4", plan.key === "build" ? "xl:-mt-12" : "")}>
                              <button
                                type="button"
                                className="flex w-full items-center justify-between gap-4 border-0 bg-transparent p-0 text-left font-[inherit] text-[#1f0a18] cursor-pointer"
                                aria-expanded={isOpen}
                                aria-controls={accordionId}
                                onClick={() => togglePricingPlan(plan.key)}
                              >
                                <div className="min-h-[1.25rem]">
                                  <div className="text-[0.78rem] font-semibold uppercase tracking-[0.18em] text-pink-dark/75">Full Details</div>
                                </div>
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[rgb(118_42_92_/_12%)] text-pink-dark">
                                  <div className="w-4 shrink-0">
                                    <PricingAccordionIcon open={isOpen} />
                                  </div>
                                </div>
                              </button>

                              <div
                                id={accordionId}
                                className={cn(
                                  "grid overflow-clip transition-[grid-template-rows] duration-300 ease-in-out",
                                  isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                                )}
                              >
                                <div className="min-h-0">
                                  <div className="mt-5 space-y-5 border-t border-[rgb(118_42_92_/_10%)] pt-5">
                                    {plan.details.map((section) => (
                                      <div key={`${plan.key}-${section.title}`}>
                                        <div className="text-sm font-semibold uppercase tracking-[0.14em] text-[#1f0a18]">{section.title}</div>
                                        <ul role="list" className="mt-3 space-y-3 list-none pl-0">
                                          {section.items.map((item) => (
                                            <li key={`${plan.key}-${section.title}-${item}`} className="mb-0">
                                              <div className="flex items-start gap-3">
                                                <div className="mt-0.5 flex w-5 shrink-0 text-pink-dark">
                                                  <PricingFeatureCheckIcon />
                                                </div>
                                                <div className="text-sm leading-relaxed text-[#4a4a4a]">{item}</div>
                                              </div>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {plan.addOns?.length ? (
                              <div className="rounded-[1.25rem] border border-[rgb(118_42_92_/_10%)] bg-[color-mix(in_srgb,var(--color-pink-dark)_5%,white)] p-4">
                                <div className="text-center text-[0.78rem] font-semibold uppercase tracking-[0.18em] text-pink-dark/75">Popular Add-On</div>
                                <div className="mt-3 space-y-3">
                                  {plan.addOns.map((addOn) => (
                                    <div key={`${plan.key}-${addOn.title}`} className="grid grid-cols-[auto_1fr] items-start gap-3">
                                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-pink-dark shadow-[inset_0_0_0_1px_rgb(118_42_92_/_10%)]">
                                        {addOn.productKey === "custom_music_mix" ? (
                                          <PricingMusicMixIcon className="w-4" />
                                        ) : (
                                          <PricingPracticeAddOnIcon className="w-4" />
                                        )}
                                      </div>
                                      <div>
                                        <div className="text-[1.05rem] font-semibold leading-snug text-[#1f0a18]">{addOn.title}</div>
                                        <p className="mt-1.5 mb-0 text-sm leading-relaxed text-[#4a4a4a]">{addOn.description}</p>
                                      </div>
                                      {addOn.productKey === "custom_music_mix" ? (
                                        <div className="col-span-2 flex justify-center">
                                          <button
                                            type="button"
                                            className={cn(
                                              "mt-1 inline-flex cursor-pointer items-center justify-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold leading-none transition-colors",
                                              isCustomMusicMixInCart
                                                ? "border-pink-dark bg-white text-pink-dark"
                                                : "border-pink-dark bg-pink-dark text-white hover:bg-[#8a356b]",
                                            )}
                                            aria-label={
                                              isCustomMusicMixInCart
                                                ? "Open cart for Custom Music Mix"
                                                : "Add Custom Music Mix to Cart"
                                            }
                                            onClick={handleCustomMusicMixAddToCart}
                                          >
                                            {isCustomMusicMixInCart ? "Added to Cart" : "Add to Cart"}
                                            <span className="flex w-3 shrink-0">
                                              <TypeCardArrowIcon className="w-full" />
                                            </span>
                                          </button>
                                        </div>
                                      ) : null}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : null}

                            {plan.footerNote ? (
                              <div className="flex items-start gap-2 rounded-[1.25rem] border border-dashed border-[rgb(118_42_92_/_18%)] bg-[color-mix(in_srgb,#fff8f5_85%,white)] px-4 py-3 text-sm leading-relaxed text-[#5a5658]">
                                <span className="mt-[3px] flex h-4 w-4 shrink-0 text-pink-dark">
                                  <PricingLightbulbIcon className="w-full" />
                                </span>
                                <span>{plan.footerNote}</span>
                              </div>
                            ) : null}

                            <Link
                              to={plan.href}
                              className={cn(
                                "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-normal rounded-large border px-8 py-4 text-center text-[clamp(1rem,0.9643rem+0.1786vw,1.125rem)] font-bold leading-none no-underline transition-transform duration-200 ease-[cubic-bezier(.39,.575,.565,1)] hover:-translate-y-[5%] active:scale-95",
                                plan.key === "build" ? "mt-0" : "mt-auto",
                                plan.featured
                                  ? "border-pink-dark bg-pink-dark text-white"
                                  : "border-[rgb(118_42_92_/_14%)] bg-[color-mix(in_srgb,var(--color-pink-dark)_10%,white)] text-pink-dark",
                              )}
                            >
                              {plan.ctaLabel}
                              <span className="flex w-3 shrink-0">
                                <TypeCardArrowIcon className="w-full" />
                              </span>
                            </Link>
                          </div>
                        </article>
                      );
                    })}
                  </div>

                  <div className="mt-8 rounded-[1.75rem] border border-[rgb(118_42_92_/_10%)] bg-white p-5 text-left shadow-[0_24px_60px_rgb(31_10_24_/_7%)] max-[767px]:p-4">
                    <div className="grid gap-5 lg:grid-cols-[minmax(0,1.45fr)_minmax(0,0.85fr)] lg:items-center">
                      <div>
                        <div className="inline-flex items-center gap-2 rounded-full bg-[color-mix(in_srgb,var(--color-pink-dark)_10%,white)] px-3 py-1 text-[0.78rem] font-semibold uppercase tracking-[0.14em] text-pink-dark">
                          <span className="flex w-4 shrink-0">
                            <PricingPracticeAddOnIcon className="w-full" />
                          </span>
                          Available with any experience
                        </div>
                        <h3 className="mt-3 font-display text-[clamp(1.5rem,1.2143rem+1.4286vw,2.5rem)] font-medium leading-[1.15] text-[#1f0a18]">
                          {pricingPracticeAddOn.title}
                        </h3>
                        <p className="mt-2.5 mb-0 max-w-[45rem] text-base leading-relaxed text-[#4a4a4a]">
                          {pricingPracticeAddOn.description}
                        </p>
                      </div>

                      <div className="rounded-[1.5rem] border border-[rgb(118_42_92_/_10%)] bg-[linear-gradient(180deg,#fff8f4_0%,#ffffff_100%)] p-4 lg:p-5">
                        <div className="text-[0.78rem] font-semibold uppercase tracking-[0.18em] text-pink-dark/75">Virtual add-on</div>
                        <div className="mt-2.5 flex items-end gap-3">
                          <div className="font-display text-[clamp(2.4rem,2.1714rem+1.1429vw,3rem)] font-semibold leading-none tracking-[-0.03em] text-[#1f0a18]">
                            {pricingPracticeAddOn.price}
                          </div>
                        </div>
                        <ul role="list" className="mt-4 space-y-2.5 list-none pl-0">
                          {pricingPracticeAddOn.bullets.map((item) => (
                            <li key={item} className="mb-0">
                              <div className="flex items-start gap-3">
                                <div className="mt-0.5 flex w-5 shrink-0 text-pink-dark">
                                  <PricingFeatureCheckIcon />
                                </div>
                                <div className="text-sm leading-relaxed text-[#4a4a4a]">{item}</div>
                              </div>
                            </li>
                          ))}
                        </ul>
                        <button
                          type="button"
                          className={cn(
                            "mt-5 inline-flex cursor-pointer items-center justify-center gap-2 rounded-large border px-7 py-3.5 text-center text-[clamp(1rem,0.9643rem+0.1786vw,1.125rem)] font-bold leading-none no-underline transition-transform duration-200 ease-[cubic-bezier(.39,.575,.565,1)] hover:-translate-y-[5%] active:scale-95",
                            isVirtualPracticeInCart
                              ? "border-pink-dark bg-white text-pink-dark"
                              : "border-pink-dark bg-pink-dark text-white",
                          )}
                          aria-label={
                            isVirtualPracticeInCart
                              ? "Open cart for Virtual Practice"
                              : "Add Virtual Practice to Cart"
                          }
                          onClick={handleVirtualPracticeAddToCart}
                        >
                          {isVirtualPracticeInCart ? "Added to Cart" : pricingPracticeAddOn.ctaLabel}
                          <span className="flex w-3 shrink-0">
                            <TypeCardArrowIcon className="w-full" />
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══ DESIGNED FOR EVERY DANCER ═══ */}
        <section className="bg-transparent">
          <div className="px-[var(--site-margin)]">
            <div className="mx-auto w-full max-w-[80rem]">
              <div className="py-[6.25rem] max-[767px]:py-16">
                <div>
                  <div className="mx-auto w-full text-center">
                    <div data-wf--eyebrow--variant="blue" className="mx-auto flex w-fit justify-start rounded-full border-[0.09rem] border-[rgb(118_42_92_/_10%)] bg-[color-mix(in_srgb,#ffffff_40%,transparent)] p-[0.38rem] font-semibold text-pink-dark">
                      <div className="flex items-center gap-1 rounded-[inherit] border-[inherit] bg-white px-[0.88rem] py-2">
                        <div className="flex w-5 flex-col">
                          <OfferEyebrowIcon />
                        </div>
                        <div className="m-0 text-[0.88rem] font-semibold leading-none text-inherit">Our Categories</div>
                      </div>
                    </div>
                    <div className="w-full pt-6 max-[767px]:pt-5" />
                    <h2 className="m-0 font-display text-[clamp(2rem,1.5714rem+2.1429vw,3.5rem)] font-medium leading-[1.15] text-[#1f0a18]">
                      Designed for <span className="text-pink-dark">Every Dancer</span>
                    </h2>
                    <div className="w-full pt-6 max-[767px]:pt-5" />
                    <p className="m-0 text-[clamp(1rem,0.9643rem+0.1786vw,1.125rem)] font-medium leading-normal text-[#4a4a4a]">Explore tutorials by occasion, group type, and skill level</p>
                  </div>

                  <div>
                    <div className="mt-12 flex flex-wrap items-start justify-center gap-12 max-[991px]:gap-8 max-[767px]:gap-6 max-[479px]:flex-col max-[479px]:items-stretch max-[479px]:gap-4" role="list">
                      {dancerTypes.map((type) => (
                        <div key={type.label} role="listitem">
                          <Link
                            to={`/dance-library?tags=${encodeURIComponent(type.label)}`}
                            className="flex items-center justify-between gap-[4.5rem] rounded-[1.25rem] border-[0.13rem] border-white bg-white p-6 capitalize text-neutral-black no-underline shadow-[0_0_0_0.13rem_#ffffff] transition-[box-shadow,border-color] duration-200 hover:border-[#faf5f2] hover:shadow-[0_0_0_0.13rem_var(--color-yellow-brand)] focus-visible:border-[#faf5f2] focus-visible:shadow-[0_0_0_0.13rem_var(--color-yellow-brand)] max-[767px]:gap-10 max-[767px]:px-5 max-[767px]:py-4 max-[479px]:gap-4"
                          >
                            <h3 className="m-0 font-display text-[clamp(1.25rem,1.0357rem+1.0714vw,2rem)] font-semibold leading-[1.2]">{type.label}</h3>
                            <div className="flex aspect-square w-8 shrink-0 items-center justify-center rounded-full bg-pink-dark text-white max-[767px]:w-7">
                              <TypeCardArrowIcon className="block h-auto w-3" />
                            </div>
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══ TESTIMONIALS ═══ */}
        <section id="backstage-talk" className="relative overflow-clip bg-background-primary py-0 scroll-mt-24">
          <div className="py-[6.25rem] max-[767px]:py-16">
            <div className="px-[var(--site-margin)]">
              <div className="mx-auto w-full max-w-[80rem]">
                <div className="flex flex-col items-center justify-start text-center">
                  <span className="flex w-fit justify-start rounded-full border-[0.09rem] border-[rgb(118_42_92_/_10%)] bg-[color-mix(in_srgb,#ffffff_40%,transparent)] p-[0.38rem] font-semibold text-pink-dark">
                    <span className="flex items-center gap-1 rounded-[inherit] border-[inherit] bg-white px-[0.88rem] py-2 text-[0.88rem] font-semibold leading-none">
                      <Sparkles className="h-5 w-5 shrink-0" />
                      Our Testimonials
                    </span>
                  </span>
                  <div className="w-full pt-6 max-[767px]:pt-5" />
                  <h2 className="m-0 font-display text-[clamp(2rem,1.5714rem+2.1429vw,3.5rem)] font-medium leading-[1.14] tracking-[-0.02em] text-[#1f0a18] max-[767px]:text-[clamp(2rem,1.5714rem+2.1429vw,3rem)]">
                    What Our <span className="text-pink-dark">Dancers</span> Are Saying
                  </h2>
                </div>
              </div>
            </div>

            <div className="mt-12 flex items-center justify-start overflow-clip max-[991px]:mt-12">
              {testimonialTracks.map((track) => (
                <div key={track.key} className="flex shrink-0 gap-10 animate-[testimonialsMove_50s_linear_infinite] motion-reduce:!animate-none max-[767px]:gap-6">
                  <div className="flex shrink-0 gap-10 pr-10 max-[767px]:gap-6 max-[767px]:pr-6">
                    {track.cards.map((testimonial) => (
                      <div key={testimonial.key} className="shrink-0">
                        <article className="flex h-full w-[min(34rem,calc(100vw-(var(--site-margin)*2)))] max-w-[34rem] shrink-0 flex-col rounded-[1.25rem] bg-white p-6 max-[991px]:w-[min(28rem,calc(100vw-(var(--site-margin)*2)))] max-[991px]:max-w-[28rem] max-[767px]:w-[min(22rem,90vw)] max-[767px]:max-w-[22rem] max-[479px]:w-[min(90vw,22rem)]">
                          <div className="flex">
                            <span className="font-display text-[1.25rem] font-medium leading-normal text-[#d99a39] max-[767px]:text-sm">{testimonial.displayIndex[0]}</span>
                            <span className="font-display text-[1.25rem] font-medium leading-normal text-[#d99a39] max-[767px]:text-sm">{testimonial.displayIndex[1]}</span>
                          </div>
                          <div className="w-full pt-6 max-[767px]:pt-5" />
                          <div className="max-w-[29rem] text-[clamp(1rem,0.9643rem+0.1786vw,1.125rem)] font-medium leading-normal text-[#1f0a18]">{testimonial.quote}</div>
                          <div className="w-full pt-6 max-[767px]:pt-5" />
                          <div className="mt-auto flex items-center justify-between gap-8 max-[767px]:gap-4">
                            <div className="flex flex-col items-start justify-start gap-2">
                              <div className="font-display text-[clamp(1.25rem,1.0357rem+1.0714vw,2rem)] font-semibold leading-[1.2] text-[#1f0a18]">{testimonial.name}</div>
                              <div className="text-base leading-normal text-[#777777]">{testimonial.role}</div>
                            </div>
                            <span className="relative aspect-square w-12 shrink-0 overflow-hidden rounded-large bg-[color-mix(in_srgb,var(--color-pink-dark)_25%,transparent)] bg-[url('https://cdn.prod.website-files.com/686229256a807206c1874545/686d6bdbb357012954de6cd1_Frame.svg')] bg-center bg-no-repeat bg-[length:24px] max-[767px]:w-12" aria-hidden="true" />
                          </div>
                        </article>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <BlogNewsletterSignupSection />
      </main>

      <Footer />
    </div>
  );
}
